import { format, parseISO } from "date-fns";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { StatusTag } from "../../../../components/atoms/Tag";
import { Tag } from "../../../../components/atoms/Tag/Tag";
import { ActivityLogCell } from "../../../../components/organisms/activityLog/ActivityLogCell";
import { CustomTooltip, EllipsisMultiText } from "../../../../components/organisms/Tooltip/Tooltip";
import { stockZoneTypeLabel, warehouseTypeLabel } from "../../../../constant/data";
import { formatAmountWithDefaults } from "../../../../helper/utils";
import { handleFilterShipmentByMilestone } from "../../../../store/slice/newInterfaceSlice";
import { handleFilterPurchaseByMilestone } from "../../../../store/slice/purchaseOrderSliceV2";
import { handleFilterTransferByMilestone } from "../../../../store/slice/TransferOrderSliceV2";
import { billSourceDocumentListCells } from "../../../bill/components/listCells";
import { integrationConfig } from "../../../integrationV1/components/IntegrationGrid";
import { ShopifyStoreActiveToggle } from "../../../integrationV1/shopify/stores/StoreActiveToggle";
import { formatProductionClock } from "../../../manufacturingOrder/formatClock";
import { PrepaymentPaymentNoCell } from "../../../prepayment/components/PrepaymentPaymentNoCell";
import { PrepaymentPurchaseOrdersCell } from "../../../prepayment/components/PrepaymentPurchaseOrdersCell";
import { ListRecordActions } from "../components/Board/ListRecordActions";
import { PurchaseOrderShipmentSubStatus } from "../../../purchaseOrder/components/PurchaseOrderShipmentSubStatus";
import { PurchaseOrderShipmentsCell } from "../../../purchaseOrder/components/PurchaseOrderShipmentsCell";
import { saleInvoiceFulfillmentListCell } from "../../../saleInvoice/layout/listCells";
import { UsersStatusCell } from "../../../user/components/UsersStatusCell";
import { timeInDays } from "../../../warehouse/manage/util";
import { facilityCostingBandListCells } from "../../../warehouseCosting/components/listCells";
import { EditAllocatedQty } from "../components/ListView/EditAllocatedQty/EditAllocatedQty";
import { ShipmentNameTagsCell } from "../components/ListView/ShipmentNameTagsCell";
import { ShipmentTrackerCell } from "../components/ListView/ShipmentTrackerCell";
import { ShipmentTransferOrdersCell } from "../components/ListView/ShipmentTransferOrdersCell";
import { Status } from "../components/Status/Status";
import { TextOnHoverWhenAdjust } from "../ToolTip/ToolTip";
import { showDateAndTime } from "./showDateAndTime";

const notAvailable = <span className="dataNotPresent">Not Available</span>;
const notApplicable = <span className="dataNotPresent">Not Applicable</span>;
const notCodeAvailable = <span className="dataNotPresent">No Code Available</span>;
const notNameAvailable = <span className="dataNotPresent">No Name Available</span>;

const displayText = (...values) =>
  values.find(value => typeof value === "string" && value.trim()) || "--";

const salesRepDisplay = rep => displayText(rep?.user?.full_name, rep?.full_name, rep);

const getCountryDisplay = value => {
  if (!value) return "";
  if (typeof value === "object") return value.name || value.id || "";
  return value;
};

const formatAddress = address => {
  if (!address) return "";
  if (address.formatted) return address.formatted;
  const { street, city, state, post_code, country } = address;
  const countryValue = getCountryDisplay(country);
  return [street, city, state, countryValue, post_code]
    .filter(value => value !== undefined && value !== null && value !== "")
    .join(", ");
};

const formatAddressV2 = address => {
  if (!address) return "";
  if (address.formatted_address) return address.formatted_address;
  const { line1, line2, suburb, city, state_name, post_code, country } = address;
  const countryValue = getCountryDisplay(country);
  return [line1, line2, suburb, city, state_name, countryValue, post_code]
    .filter(value => value !== undefined && value !== null && value !== "")
    .join(", ");
};

const formatDocumentAddress = (snapshot, liveAddress) =>
  formatAddress(snapshot) || formatAddress(liveAddress);

const SHOPIFY_SETUP_ISSUE_HINTS = {
  location_mapping: "Map Shopify locations to Orderlink facilities.",
  price_list: "Assign a sales price list.",
  products_not_linked: "Run Sync from Shopify to link products by SKU.",
};

/**
 * Status tag for a Shopify store row from ``is_authenticated`` and the
 * backend's ``setup_issues`` codes.
 *
 * @param {boolean} isAuthenticated Store OAuth state.
 * @param {string[]} setupIssues Codes from the store API.
 * @return {{label: string, tone: string, hint: (string|undefined)}}
 */
const shopifyStoreStatus = (isAuthenticated, setupIssues) => {
  if (!isAuthenticated) {
    return {
      label: "Not Connected",
      tone: "In Active",
      hint: "Open Orderlink from this store's Shopify admin to reconnect it.",
    };
  }
  if (setupIssues.includes("reauthorize")) {
    return {
      label: "Reconnect Needed",
      tone: "On Hold",
      hint: "Open Orderlink from this store's Shopify admin to approve the latest access.",
    };
  }
  const hints = setupIssues.map(code => SHOPIFY_SETUP_ISSUE_HINTS[code]).filter(Boolean);
  if (hints.length) {
    return { label: "Setup Incomplete", tone: "Pending", hint: hints.join(" ") };
  }
  return { label: "Connected", tone: "Active", hint: undefined };
};

/**
 * Format a line discount for detail tables.
 *
 * UI create/update only writes percentage discounts. When type is missing
 * (common for 0% lines), default to percentage so rows do not show "$0.00".
 *
 * @param {object} row Line payload with discount / discount_type.
 * @param {object} [options]
 * @param {string} [options.currencySymbol="$"] Symbol for fixed-amount discounts.
 * @param {function} [options.formatAmount] Formatter from useFormattedAmount /
 *   formatAmountWithDefaults.
 * @returns {string}
 */
export const formatLineDiscount = (
  row,
  { currencySymbol = "$", formatAmount = formatAmountWithDefaults } = {},
) => {
  const amount = formatAmount(row?.discount ?? 0);
  const display = amount == null || amount === "" ? formatAmount(0) : amount;
  const typeId = row?.discount_type?.id ?? row?.discount_type;
  if (typeId === "fixed_amount") {
    return `${currencySymbol} ${display}`;
  }
  return `${display}%`;
};

export { formatAddressV2, formatDocumentAddress };

/**
 * Column cell renderers for module list tables.
 *
 * Each key (for example ``purchase``) returns table cells keyed by column
 * header labels such as STATUS, FACILITY, and TYPE.
 *
 * @const
 * @type {Object<string, function(Object): Object>}
 */
export const listDataElements = {
  picklist: ({ order, columnHeaderWidth }) => {
    const pickDate = order?.pick_date ?? "--";

    return {
      TYPE: <td key="type">{order?.type?.name ?? "-"}</td>,
      STATUS: (
        <td key="status" className="status">
          <Status statusName={order?.status?.name ?? order?.status} />
        </td>
      ),
      FACILITY: order?.warehouse ? (
        <TextOnHoverWhenAdjust
          key="warehouse"
          text={order.warehouse.name}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ) : (
        <td key="warehouse" className="wareHouse">
          {notApplicable}
        </td>
      ),
      "PICK DATE": (
        <td key="pick_date" className="centerAlign" title={pickDate}>
          {pickDate}
        </td>
      ),
      "SALES ORDERS": (
        <td key="sales_orders">
          <EllipsisMultiText
            text={order?.sales_orders?.map(item => item.name)?.join(", ")}
            width={columnHeaderWidth["sales orders"] || 100}
          >
            {order?.sales_orders?.length
              ? order.sales_orders.map((item, i) => (
                  <Tag key={`so-${order.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      CUSTOMERS: (
        <td key="customers">
          <EllipsisMultiText
            text={order?.customers?.map(item => item.display_name || item.name)?.join(", ")}
            width={columnHeaderWidth["customers"] || 100}
          >
            {order?.customers?.length
              ? order.customers.map((item, i) => (
                  <Tag key={`cust-${order.id}-${i}`} className="in-table">
                    {item.display_name || item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      "REQUESTED SHIP DATE": (
        <td key="requested_ship_date" className="centerAlign">
          {order?.requested_ship_date || "--"}
        </td>
      ),
    };
  },
  shipment: ({ order, columnHeaderWidth, onTrackingUpdated, recordActionHandlers }) => {
    const [eta, etaWithTime] = showDateAndTime(order.eta) || [];
    const [creationDate, creationDateWithTime] = showDateAndTime(order.date_of_creation);
    return {
      ACTIONS: (
        <td key="actions" className="centerAlign listRecordActionsCell">
          {recordActionHandlers ? (
            <ListRecordActions order={order} {...recordActionHandlers} />
          ) : null}
        </td>
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={order.status} />
        </td>
      ),
      ETA: (
        <td key="eta" className="centerAlign" title={etaWithTime}>
          {" "}
          {eta ? eta : "No ETA"}
        </td>
      ),
      "ORDER DATE": (
        <td key="date_of_creation" className="centerAlign" title={creationDateWithTime}>
          {" "}
          {creationDate ? creationDate : ""}
        </td>
      ),
      "CREATED BY": order.created_by ? (
        <TextOnHoverWhenAdjust
          key="creator hovered"
          text={order.created_by.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ) : (
        <td key="creator" className="userPic">
          {notAvailable}
        </td>
      ),
      "FROM FACILITY": (
        <td key="from warehouse" className="wareHouse">
          <ShipmentNameTagsCell
            items={order.from_warehouses}
            width={columnHeaderWidth["from facility"] || 100}
          />
        </td>
      ),
      "TO FACILITY": (
        <TextOnHoverWhenAdjust
          key="to whs hovered"
          text={
            order.to_warehouse_name ||
            (typeof order.to_warehouse === "string" ? order.to_warehouse : order.to_warehouse?.name)
          }
          widthOfElement={columnHeaderWidth["to facility"] || 100}
        />
      ),
      SUPPLIER: (
        <td key="supplier" className="wareHouse">
          <ShipmentNameTagsCell
            items={order.suppliers}
            width={columnHeaderWidth["supplier"] || 100}
          />
        </td>
      ),
      NAME: (
        <TextOnHoverWhenAdjust
          key="product name"
          text={order?.reference}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      "PURCHASE ORDERS": (
        <td key="purchase_orders">
          <PrepaymentPurchaseOrdersCell
            order={order}
            width={columnHeaderWidth["purchase orders"] || 100}
          />
        </td>
      ),
      "TRANSFER ORDERS": (
        <td key="transfer_orders">
          <ShipmentTransferOrdersCell
            order={order}
            width={columnHeaderWidth["transfer orders"] || 100}
          />
        </td>
      ),
      TRACKER: <ShipmentTrackerCell order={order} onTrackingUpdated={onTrackingUpdated} />,
    };
  },
  shipmentLeg: ({ order, columnHeaderWidth }) => {
    const [etd, etdWithTime] = showDateAndTime(order.estimated_departure_at) || [];
    const [eta, etaWithTime] = showDateAndTime(order.estimated_arrival_at) || [];
    const [atd, atdWithTime] = showDateAndTime(order.actual_departure_at) || [];
    const [ata, ataWithTime] = showDateAndTime(order.actual_arrival_at) || [];

    return {
      "SHIPMENT NAME": (
        <td key="shipment_name" className="projectName">
          {order.shipment?.name ? (
            <Link to={`/shipment/leg/${order.id}`}>{order.shipment.name}</Link>
          ) : (
            notAvailable
          )}
        </td>
      ),

      SEQUENCE: <td key="sequence">{order.sequence ?? "--"}</td>,
      STATUS: (
        <td key="status" className="status">
          <Status statusName={order.status?.name || order.status} />
        </td>
      ),
      TYPE: (
        <TextOnHoverWhenAdjust
          key="type"
          text={order.leg_type?.name || order.leg_type}
          widthOfElement={columnHeaderWidth["type"] || 100}
        />
      ),
      "TRANSPORT MODE": (
        <TextOnHoverWhenAdjust
          key="transport_mode"
          text={order.transport_mode?.name || order.transport_mode}
          widthOfElement={columnHeaderWidth["transport mode"] || 100}
        />
      ),
      CARRIER: (
        <TextOnHoverWhenAdjust
          key="carrier"
          text={order.carrier}
          widthOfElement={columnHeaderWidth["carrier"] || 100}
        />
      ),
      "VEHICLE / VESSEL": (
        <TextOnHoverWhenAdjust
          key="vehicle"
          text={order.vehicle_reference}
          widthOfElement={columnHeaderWidth["vehicle / vessel"] || 100}
        />
      ),
      ORIGIN: (
        <TextOnHoverWhenAdjust
          key="origin"
          text={order.origin}
          widthOfElement={columnHeaderWidth["origin"] || 100}
        />
      ),
      DESTINATION: (
        <TextOnHoverWhenAdjust
          key="destination"
          text={order.destination}
          widthOfElement={columnHeaderWidth["destination"] || 100}
        />
      ),
      "DELIVER TO": (
        <TextOnHoverWhenAdjust
          key="deliver_to"
          text={order.deliver_to}
          widthOfElement={columnHeaderWidth["deliver to"] || 100}
        />
      ),
      ETD: (
        <td key="etd" className="centerAlign" title={etdWithTime}>
          {etd ? etd : "--"}
        </td>
      ),
      ETA: (
        <td key="eta" className="centerAlign" title={etaWithTime}>
          {eta ? eta : "--"}
        </td>
      ),
      ATD: (
        <td key="atd" className="centerAlign" title={atdWithTime}>
          {atd ? atd : "--"}
        </td>
      ),
      ATA: (
        <td key="ata" className="centerAlign" title={ataWithTime}>
          {ata ? ata : "--"}
        </td>
      ),
    };
  },
  purchase: ({ order, columnHeaderWidth, formattedAmount, recordActionHandlers }) => {
    const formatAmt = formattedAmount || (value => value ?? notAvailable);
    const currencySymbol = order.currency?.symbol ?? "";
    const [eta, etaWithTime] = showDateAndTime(order.estimated_date);
    const [creationDate, creationDateWithTime] = showDateAndTime(order.create_date);
    return {
      "ORDER NAME": (
        <TextOnHoverWhenAdjust
          key="user_reference"
          text={order.user_reference || notAvailable}
          widthOfElement={columnHeaderWidth["order name"] || 160}
        />
      ),
      ACTIONS: (
        <td key="actions" className="centerAlign listRecordActionsCell">
          {recordActionHandlers ? (
            <ListRecordActions order={order} {...recordActionHandlers} />
          ) : null}
        </td>
      ),
      STATUS: (
        <td key="status" className="status">
          <PurchaseOrderShipmentSubStatus order={order} />
        </td>
      ),
      SHIPMENTS: (
        <td key="shipments">
          <PurchaseOrderShipmentsCell order={order} width={columnHeaderWidth["shipments"] || 100} />
        </td>
      ),
      ETA: (
        <td key="eta" className="centerAlign" title={etaWithTime}>
          {" "}
          {eta ? eta : "No ETA"}
        </td>
      ),
      "CREATE DATE": (
        <td key="date_of_creation" className="centerAlign" title={creationDateWithTime}>
          {" "}
          {creationDate ? creationDate : ""}
        </td>
      ),
      "CREATED BY": order.creator ? (
        <TextOnHoverWhenAdjust
          key="created by hovered"
          text={order.creator}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ) : (
        <td key="creator" className="userPic">
          {notAvailable}
        </td>
      ),
      FACILITY: order.warehouse ? (
        <TextOnHoverWhenAdjust
          key="fromWarehouse"
          text={order.warehouse}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ) : (
        <td key="from warehouse" className="wareHouse">
          {notApplicable}
        </td>
      ),
      SUPPLIER: order.supplier ? (
        <TextOnHoverWhenAdjust
          key="supplier"
          text={order.supplier}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ) : (
        <td key="supplier" className="supplier">
          {notApplicable}
        </td>
      ),
      TYPE: (
        <td key="type" className="orderType">
          {order.type ? order.type?.name || order.type : notApplicable}
        </td>
      ),
      "PO TOTAL": (
        <td key="po_total" className="rightAlign">
          {order.total_amount != null
            ? `${currencySymbol} ${formatAmt(order.total_amount)}`
            : notApplicable}
        </td>
      ),
      PAYMENTS: (
        <td key="payments" className="rightAlign">
          {order.total_payments_allocated != null
            ? `${currencySymbol} ${formatAmt(order.total_payments_allocated)}`
            : notApplicable}
        </td>
      ),
      OUTSTANDING: (
        <td key="outstanding" className="rightAlign">
          {order.payment_outstanding != null
            ? `${currencySymbol} ${formatAmt(order.payment_outstanding)}`
            : notApplicable}
        </td>
      ),
      "PAYMENT STATUS": (
        <td key="payment_status" className="status">
          <Status statusName={order.payment_status?.name ?? order.payment_status} />
        </td>
      ),
    };
  },
  transfer: ({ order, columnHeaderWidth }) => {
    const [creationDate, creationDateWithTime] = showDateAndTime(order.create_date);
    return {
      STATUS: (
        <td key="status" className="status">
          <Status statusName={order.status.name} />
        </td>
      ),
      "CONSOLIDATION STATUS": (
        <td key="status consildation" className="status">
          <Status statusName={order.consolidation_status} />
        </td>
      ),
      "CREATE DATE": (
        <td key="date_of_creation" className="centerAlign" title={creationDateWithTime}>
          {" "}
          {creationDate ? creationDate : ""}
        </td>
      ),
      "CREATED BY": (
        <td key="creator" className="userPic">
          <div className="creatorName">
            {order.created_by ? order.created_by.full_name : notAvailable}
          </div>
        </td>
      ),
      "FROM FACILITY":
        order.from_warehouse_name || order.from_warehouse ? (
          <TextOnHoverWhenAdjust
            key={"from whs"}
            text={order.from_warehouse_name || order.from_warehouse?.name || order.from_warehouse}
            widthOfElement={columnHeaderWidth["from facility"] || 100}
          />
        ) : (
          <td key="from warehouse" className="wareHouse">
            {notApplicable}
          </td>
        ),
      "TO FACILITY": (
        <TextOnHoverWhenAdjust
          key={"to whs"}
          text={order.to_warehouse_name || order.to_warehouse?.name || order.to_warehouse}
          widthOfElement={columnHeaderWidth["to facility"] || 100}
        />
      ),
      "ERP SUPPLIER": order.erp_supplier ? (
        <TextOnHoverWhenAdjust
          key={"erp supplier"}
          text={order.erp_supplier}
          widthOfElement={columnHeaderWidth["erp supplier"] || 100}
        />
      ) : (
        <td key="supplier" className="supplier">
          {notApplicable}
        </td>
      ),
    };
  },
  customerReturn: ({ order, columnHeaderWidth, formattedAmount }) => {
    const [creationDate, creationDateWithTime] = showDateAndTime(order.date_of_creation);
    const [returnDate, returnDateWithTime] = showDateAndTime(order.return_date);
    return {
      "RETURN NO": (
        <td key="cr_no" className="projectName">
          <Link to={`/customer_return/${order.id}`}>{order.name}</Link>
        </td>
      ),
      CUSTOMER:
        order.customer_name || order.customer ? (
          <TextOnHoverWhenAdjust
            key="customer"
            text={order.customer_name || order.customer?.display_name}
            widthOfElement={columnHeaderWidth["customer"] || 100}
          />
        ) : (
          <td key="customer">{notAvailable}</td>
        ),
      "RETURN TYPE": (
        <td key="return_type" className="centerAlign">
          {order.return_type || "--"}
        </td>
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={order.status?.name || order.status} />
        </td>
      ),
      "SALES NO": order.sale_order ? (
        <td key="sales_no" className="projectName">
          <Link to={`/sales/order/${order.sale_order.id}`}>
            {order.sale_order.name || order.sale_order.display_name}
          </Link>
        </td>
      ) : (
        <td key="sales_no">--</td>
      ),
      "INVOICE NO": <td key="invoice_no">{order.invoice_no || "--"}</td>,
      "CREATE DATE": (
        <td key="created_date" className="centerAlign" title={creationDateWithTime}>
          {creationDate || "--"}
        </td>
      ),
      "RETURN DATE": (
        <td key="return_date" className="centerAlign" title={returnDateWithTime}>
          {returnDate || "--"}
        </td>
      ),
      SALESPERSON: order.salesperson ? (
        <TextOnHoverWhenAdjust
          key="salesperson"
          text={order.salesperson.full_name || order.salesperson.name}
          widthOfElement={columnHeaderWidth["salesperson"] || 100}
        />
      ) : (
        <td key="salesperson">{notAvailable}</td>
      ),
      CURRENCY: (
        <td key="currency" className="centerAlign">
          {order.currency?.name || order.currency?.code || "--"}
        </td>
      ),
      "TO FACILITY": order.to_warehouse ? (
        <TextOnHoverWhenAdjust
          key="to_warehouse"
          text={order.to_warehouse.name}
          widthOfElement={columnHeaderWidth["to facility"] || 100}
        />
      ) : (
        <td key="to_warehouse">{notApplicable}</td>
      ),
      "CUSTOMER REFERENCE": (
        <TextOnHoverWhenAdjust
          key="customer_reference"
          text={order.customer_reference}
          widthOfElement={columnHeaderWidth["customer reference"] || 100}
        />
      ),
      "SUB TOTAL": (
        <td key="subtotal" className="rightAlign">
          {formattedAmount(order.sub_total)}
        </td>
      ),
      TAX: (
        <td key="tax" className="rightAlign">
          {formattedAmount(order.tax)}
        </td>
      ),
      TOTAL: (
        <td key="total" className="rightAlign">
          {formattedAmount(order.total)}
        </td>
      ),
    };
  },
  inventory: ({ order, columnHeaderWidth, formattedAmount, onOpenIncomingDrawer }) => {
    const { incoming_qty, incoming = [] } = order;
    const formatIncomingEta = value => (value ? format(parseISO(value), "dd MMM yyyy") : "no ETA");
    const incomingLineLabel = line => {
      const qtyLabel = formattedAmount(line.qty);
      return line.eta
        ? `${line.name}: ${qtyLabel} due ${formatIncomingEta(line.eta)}`
        : `${line.name}: ${qtyLabel} due ${formatIncomingEta(null)}`;
    };
    const hasIncoming = Number(incoming_qty) > 0;
    const incomingTooltipText = hasIncoming ? incoming.map(incomingLineLabel).join(", ") : "";

    return {
      CATEGORIES: (
        <td key="categories">
          <EllipsisMultiText
            text={order.product?.categories?.map(item => item.name)?.join(", ")}
            width={columnHeaderWidth["categories"] || 100}
          >
            {order.product?.categories?.length
              ? order.product?.categories?.map((item, i) => (
                  <Tag key={`cat-${order.product.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product hovered"
          text={order.product.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      "PRODUCT NAME": (
        <TextOnHoverWhenAdjust
          key="name hovered"
          text={order.product.name}
          widthOfElement={columnHeaderWidth["product name"] || 100}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="facility hovered"
          text={order.warehouse.name}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),
      SELLABLE: (
        <TextOnHoverWhenAdjust
          key=" sellable hovered"
          text={formattedAmount(order.sellable)}
          widthOfElement={columnHeaderWidth["sellable"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      ALLOCATED: (
        <TextOnHoverWhenAdjust
          key=" allocated hovered"
          text={formattedAmount(order.allocated)}
          widthOfElement={columnHeaderWidth["allocated"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      RESERVED: (
        <TextOnHoverWhenAdjust
          key=" reserved hovered"
          text={formattedAmount(order.reserved)}
          widthOfElement={columnHeaderWidth["reserved"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "ON HOLD": (
        <TextOnHoverWhenAdjust
          key=" on_hold hovered"
          text={formattedAmount(order.on_hold)}
          widthOfElement={columnHeaderWidth["on hold"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      REJECTED: (
        <TextOnHoverWhenAdjust
          key=" rejected hovered"
          text={formattedAmount(order.rejected)}
          widthOfElement={columnHeaderWidth["rejected"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      TOTAL: (
        <TextOnHoverWhenAdjust
          key=" total hovered"
          text={formattedAmount(order.total)}
          widthOfElement={columnHeaderWidth["total"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      NSP: (
        <TextOnHoverWhenAdjust
          key=" nsp hovered"
          text={formattedAmount(order.nsp)}
          widthOfElement={columnHeaderWidth["nsp"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "AVG COST": (
        <TextOnHoverWhenAdjust
          key=" average_cost hovered"
          text={formattedAmount(order.average_cost)}
          widthOfElement={columnHeaderWidth["avg cost"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "STOCK VALUE": (
        <TextOnHoverWhenAdjust
          key=" total_value hovered"
          text={formattedAmount(order.total_value)}
          widthOfElement={columnHeaderWidth["stock value"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      INCOMING: (
        <td key="incoming" className="rightAlign">
          {hasIncoming ? (
            <CustomTooltip text={incomingTooltipText} tooltipClassName="limit">
              <span
                role="button"
                tabIndex={0}
                className="incoming-pill-trigger"
                onClick={event => {
                  event.stopPropagation();
                  onOpenIncomingDrawer?.({
                    productName: order.product?.name,
                    productNumber: order.product?.number,
                    facilityName: order.warehouse?.name,
                    incoming_qty,
                    incoming,
                  });
                }}
                onKeyDown={event => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  event.stopPropagation();
                  onOpenIncomingDrawer?.({
                    productName: order.product?.name,
                    productNumber: order.product?.number,
                    facilityName: order.warehouse?.name,
                    incoming_qty,
                    incoming,
                  });
                }}
              >
                <Tag className="in-table incoming-pill">{formattedAmount(incoming_qty)}</Tag>
              </span>
            </CustomTooltip>
          ) : (
            "--"
          )}
        </td>
      ),
    };
  },
  inventoryDetail: ({ order, columnHeaderWidth, formattedAmount }) => {
    const [createDate, createDateWithTime] = showDateAndTime(order.date_of_creation);
    return {
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product hovered"
          text={order.stock_summary.product?.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="facility hovered"
          text={order.stock_summary.warehouse?.name}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),
      QUANTITY: (
        <TextOnHoverWhenAdjust
          key=" qty hovered"
          text={formattedAmount(order.qty)}
          widthOfElement={columnHeaderWidth["quantity"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      ALLOCATED: (
        <TextOnHoverWhenAdjust
          key="allocated hovered"
          text={formattedAmount(order.allocated_qty)}
          widthOfElement={columnHeaderWidth["allocated"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      ALLOCATABLE: (
        <TextOnHoverWhenAdjust
          key=" allocatable hovered"
          text={formattedAmount(order.allocatable_qty)}
          widthOfElement={columnHeaderWidth["allocatable"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "UNIT COST": (
        <TextOnHoverWhenAdjust
          key="unit price hovered"
          text={formattedAmount(order.unit_price)}
          widthOfElement={columnHeaderWidth["unit cost"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      UOM: (
        <TextOnHoverWhenAdjust
          key="uom hovered"
          text={order.uom}
          widthOfElement={columnHeaderWidth["uom"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
        />
      ),
      "BATCH NO": (
        <TextOnHoverWhenAdjust
          key="batch no hovered"
          text={order.batch_number}
          widthOfElement={columnHeaderWidth["batch no"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
        />
      ),

      "SERIAL NO": (
        <TextOnHoverWhenAdjust
          key="serial no hovered"
          text={order.lot_number}
          widthOfElement={columnHeaderWidth["serial no"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
        />
      ),

      "OTHER COSTS": (
        <TextOnHoverWhenAdjust
          key="landed cost  hovered"
          text={formattedAmount(order.landed_cost)}
          widthOfElement={columnHeaderWidth["other costs"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "TOTAL LANDED COSTS": (
        <TextOnHoverWhenAdjust
          key=" total_unit_price hovered"
          text={formattedAmount(order.total_unit_price)}
          widthOfElement={columnHeaderWidth["total landed costs"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),

      LOCATION: (
        <TextOnHoverWhenAdjust
          key="location hovered"
          text={order.stock_location?.name}
          widthOfElement={columnHeaderWidth["location"] || 100}
        />
      ),

      ZONE: (
        <TextOnHoverWhenAdjust
          key="zone hovered"
          text={order.stock_location?.stock_zone?.name}
          widthOfElement={columnHeaderWidth["zone"] || 100}
        />
      ),

      "CREATE DATE": (
        <td key="eta" className="centerAlign" title={createDateWithTime}>
          {createDate ? <> {createDate}</> : "--"}
        </td>
      ),

      STATUS: (
        <td key={"status"} className="status">
          <Status statusName={order.status.name} />
        </td>
      ),
      "CREATED BY": (
        <td key={"created by"} className="userPic">
          {order.created_by ? order.created_by?.full_name : notAvailable}
        </td>
      ),
    };
  },
  /**
   * Constructs the cell elements for the Inventory Transactions list view table.
   *
   * Formats product number, facility name, reference identifier, timestamps,
   * quantity changes, and stock-on-hand snapshots for ledger transparency.
   * Resolves values from top-level annotated fields or nested parent lines.
   *
   * @param {{
   *   order: Object<string, *>,
   *   columnHeaderWidth: Object<string, number>,
   *   formattedAmount: function(*): string
   * }} params
   * @return {Object<string, *>}
   */
  inventoryTransactions: ({ order, columnHeaderWidth, formattedAmount }) => {
    const [dot, dotWithTime] = showDateAndTime(order.date_of_creation);

    const product = order.product?.number || order.product_number || "--";
    const reference = order.reference || order.stock_adjust_ref_no || "--";
    const sohValue = order.stock_qty || "--";
    const warehouse = order.warehouse?.name || order.warehouse_name || "--";

    return {
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product hovered"
          text={product}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="facility hovered"
          text={warehouse}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),
      QUANTITY: (
        <TextOnHoverWhenAdjust
          key=" qty hovered"
          text={formattedAmount(order.qty)}
          widthOfElement={columnHeaderWidth["quantity"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      SOH: (
        <td key="allocated" className="rightAlign">
          {formattedAmount(sohValue)}
        </td>
      ),
      REASON: (
        <TextOnHoverWhenAdjust
          key="reason hovered"
          text={order.reason?.reason || "--"}
          widthOfElement={columnHeaderWidth["reason"] || 100}
        />
      ),
      REFERENCE: (
        <TextOnHoverWhenAdjust
          key="reference hovered"
          text={reference}
          widthOfElement={columnHeaderWidth["reference"] || 100}
        />
      ),
      "DATE OF TRANSACTION": (
        <td key={"dotransaction"} className="centerAlign" title={dotWithTime}>
          {dot ? dot : ""}
        </td>
      ),
    };
  },
  allocation: ({ order, allocationFor, columnHeaderWidth, formattedAmount }) => {
    const [time, etaWithTime] = showDateAndTime(order.eta || order.requested_ship_date);
    return {
      "MANF NO": (
        <td key="MANF NO" className="leftAlign projectName">
          <Link to={`/manufacturing-order/${order.manufacturing_order?.id}`}>
            <TextOnHoverWhenAdjust
              text={order.manufacturing_order?.name || "--"}
              widthOfElement={columnHeaderWidth["manf no"] || 100}
            />
          </Link>
        </td>
      ),
      "ORD NO": (
        <td key="ORD NO" className="leftAlign projectName">
          <Link
            to={
              (allocationFor === "purchase" ? "/orders/purchase/" : "/transfer_order/") +
              `${order[selectNameForAllocation[allocationFor]]?.id}`
            }
          >
            <TextOnHoverWhenAdjust
              text={order[selectNameForAllocation[allocationFor]]?.name}
              widthOfElement={columnHeaderWidth["ord no"] || 100}
            />
          </Link>
        </td>
      ),
      "ORDER NAME": (
        <TextOnHoverWhenAdjust
          key="allocation-order-name"
          text={
            allocationFor === "purchase"
              ? order.purchase_order?.user_reference || notAvailable
              : notAvailable
          }
          widthOfElement={columnHeaderWidth["order name"] || 160}
        />
      ),
      "SALES NO": (
        <td key="SALES NO" className="leftAlign projectName">
          <Link to={`/sales/order/${order.sale_order?.id}`}>
            <TextOnHoverWhenAdjust
              text={order.sale_order?.name}
              widthOfElement={columnHeaderWidth["sales no"] || 100}
            />
          </Link>
        </td>
      ),
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product hovered"
          text={order.product?.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      SUPPLIER: (
        <TextOnHoverWhenAdjust
          key="supplier hovered"
          text={order[selectNameForAllocation[allocationFor]]?.supplier}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ),
      CUSTOMER: (
        <TextOnHoverWhenAdjust
          key="customer hovered"
          text={order.customer?.display_name}
          widthOfElement={columnHeaderWidth["customer"] || 100}
        />
      ),
      "FROM FACILITY": order[selectNameForAllocation[allocationFor]] && (
        <TextOnHoverWhenAdjust
          key="from whs hovered"
          text={order[selectNameForAllocation[allocationFor]]?.from_warehouse?.name}
          widthOfElement={columnHeaderWidth["from facility"] || 100}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="facility hovered"
          text={order.warehouse?.name || order.from_warehouse?.name}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),
      QUANTITY: (
        <td className="rightAlign" key="qty">
          {formattedAmount(order.qty)}
        </td>
      ),
      UOM: (
        <TextOnHoverWhenAdjust
          key="uom hovered"
          text={order.uom?.name || "--"}
          widthOfElement={columnHeaderWidth["uom"] || 80}
        />
      ),
      "WASTAGE %": (
        <td className="rightAlign" key="wastage_percent">
          {formattedAmount(order.wastage_percent)}
        </td>
      ),
      "TO FACILITY": order[selectNameForAllocation[allocationFor]] && (
        <TextOnHoverWhenAdjust
          key="to whs hovered"
          text={order[selectNameForAllocation[allocationFor]]?.to_warehouse}
          widthOfElement={columnHeaderWidth["to facility"] || 100}
        />
      ),
      "REQ DATE": (
        <td key="eta" className="centerAlign" title={etaWithTime}>
          {" "}
          {time ? <> {time}</> : "--"}
        </td>
      ),
      "REQUESTED SHIP DATE": (
        <td key="requested_ship_date" className="centerAlign" title={etaWithTime}>
          {" "}
          {time ? <> {time}</> : "--"}
        </td>
      ),
      BATCH: (
        <TextOnHoverWhenAdjust
          key="batch hovered"
          text={order.batch || "--"}
          widthOfElement={columnHeaderWidth["batch"] || 100}
        />
      ),
      SERIAL: (
        <TextOnHoverWhenAdjust
          key="serial hovered"
          text={order.serial || "--"}
          widthOfElement={columnHeaderWidth["serial"] || 100}
        />
      ),
      LOCATION: (
        <TextOnHoverWhenAdjust
          key="location hovered"
          text={order.location || "--"}
          widthOfElement={columnHeaderWidth["location"] || 100}
        />
      ),
      REQUIRED: (
        <td className="rightAlign" key="required qty">
          {formattedAmount(order.required_qty ?? order.ordered_qty ?? order.quantity)}
        </td>
      ),
      ALLOCATABLE: (
        <td className="rightAlign" key="allocatable">
          {formattedAmount(order.allocatable ?? 0)}
        </td>
      ),
      "ALLOCATABLE QTY": (
        <td className="rightAlign" key="allocatable-qty">
          {formattedAmount(order.allocatable ?? order.allocatable_qty ?? 0)}
        </td>
      ),
      ALLOCATED: (
        <EditAllocatedQty
          key={order.id}
          id={order.id}
          allocated_qty={order.allocated_qty}
          allocationFor={allocationFor}
        />
      ),
      "ALLOCATED QTY": (
        <EditAllocatedQty
          key={`${order.id}-allocated-qty`}
          id={order.id}
          allocated_qty={order.allocated_qty}
          allocationFor={allocationFor}
        />
      ),
      PICKED: (
        <td className="rightAlign" key="picked">
          {formattedAmount(order.picked_qty || 0)}
        </td>
      ),
      DISPATCHED: (
        <td className="rightAlign" key="dis">
          {formattedAmount(order.dispatched_qty)}
        </td>
      ),
      "ALCTD VALUE": (
        <td className="rightAlign" key="allvalue">
          {formattedAmount(order.allocated_value)}
        </td>
      ),
      "ALCTD CBM": (
        <td className="rightAlign" key="allocatedcbm">
          {formattedAmount(order.allocated_cbm)}
        </td>
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={order?.status?.name || order.status} />
        </td>
      ),
    };
  },
  receipt: ({ order, columnHeaderWidth }) => {
    const {
      consignment = {},
      created_by = {},
      document_number,
      document_type = {},
      name: receiptName,
      id: receiptId,
      overall_status,
      posting_date,
      shipment_number,
    } = order;

    const { full_name } = created_by || {};
    const { id: consignmentId, name: consignmentName, reference } = consignment || {};
    const { name: documentName } = document_type || {};

    return {
      "RECEIPT NO": (
        <td key="receipt_no" className="projectName">
          {receiptName ? <Link to={`/receipts/${receiptId}`}>{receiptName}</Link> : notAvailable}
        </td>
      ),
      "DOCUMENT NUMBER": <td key="document_number">{document_number ?? notAvailable}</td>,
      "DOCUMENT TYPE": (
        <TextOnHoverWhenAdjust
          text={documentName}
          widthOfElement={columnHeaderWidth["document type"] || 100}
        />
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={overall_status} />
        </td>
      ),
      "SHIPMENT NUMBER": <td key="shipment_number">{shipment_number ?? notAvailable}</td>,
      SHIPMENT: (
        <TextOnHoverWhenAdjust
          text={
            <Link
              to={`/shipment/po-to/${consignmentId}`}
              style={{ color: "#1249bf", fontWeight: 500 }}
            >
              {reference || consignmentName || "--"}
            </Link>
          }
          widthOfElement={columnHeaderWidth["shipment"] || 100}
        />
      ),
      "TO FACILITY": (
        <TextOnHoverWhenAdjust
          text={order.to_warehouse?.name}
          widthOfElement={columnHeaderWidth["to facility"] || 100}
        />
      ),
      "POSTING DATE": <td key="posting_date">{posting_date}</td>,
      "CREATED BY": <td key="created_by">{full_name ?? notAvailable}</td>,
    };
  },
  receiptAdjustment: ({ order, columnHeaderWidth }) => {
    const {
      created_by = {},
      document_number,
      adjusted_from,
      document_type,
      name: receiptAdjustmentName,
      id: receiptAdjustmentId,
      overall_status,
      posting_date,
      consignment,
    } = order;

    const { full_name } = created_by || {};
    const [pDate] = showDateAndTime(posting_date);

    return {
      "RECEIPT ADJUSTMENT NO": (
        <td key="receipt_adjustment_no" className="projectName">
          {receiptAdjustmentName ? (
            <Link to={`/receipt_adjustment/${receiptAdjustmentId}`}>{receiptAdjustmentName}</Link>
          ) : (
            notAvailable
          )}
        </td>
      ),
      "RECEIPT NO": (
        <td key="receipt_no" className="projectName">
          {adjusted_from?.id ? (
            <Link to={`/receipts/${adjusted_from.id}`}>{adjusted_from.name}</Link>
          ) : (
            notAvailable
          )}
        </td>
      ),
      "DOCUMENT NO": <td key="document_no">{document_number ?? notAvailable}</td>,
      "DOCUMENT TYPE": (
        <TextOnHoverWhenAdjust
          text={document_type}
          widthOfElement={columnHeaderWidth["document type"] || 100}
        />
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={overall_status} />
        </td>
      ),
      "POSTING DATE": (
        <td key="posting_date" className="centerAlign">
          {pDate}
        </td>
      ),
      "SHIPMENT NO": consignment?.id ? (
        <td key="shipmentno" className="projectName">
          <Link to={`/shipment/po-to/${consignment.id}`}>{consignment.name} </Link>
        </td>
      ) : (
        <td>{notAvailable}</td>
      ),
      "CREATED BY": <td key="created_by">{full_name ?? notAvailable}</td>,
    };
  },
  bill: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      create_date,
      currency,
      due_date,
      reference_no,
      sub_total,
      total_amount,
      supplier,
      supplier_name,
      tax_amount,
      name,
    } = order || {};

    const [billDate, billDateWithTime] = showDateAndTime(create_date) || [];
    const [dueDate, dueDateWithTime] = showDateAndTime(due_date) || [];

    const supplierName = supplier_name || supplier?.name || notAvailable;
    const currencyField = currency?.code ?? currency?.symbol ?? "";

    return {
      "BILL NO": (
        <td key="bill_no" className="projectName">
          {name ? <Link to={`/bills/${order.id}`}>{name}</Link> : notAvailable}
        </td>
      ),
      SUPPLIER: (
        <TextOnHoverWhenAdjust
          key={"supplier"}
          text={supplierName}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ),
      "BILL DATE": (
        <td key="bill_date" title={billDateWithTime} className="centerAlign">
          {billDate ?? ""}
        </td>
      ),
      "DUE DATE": (
        <td key="due_date" title={dueDateWithTime} className="centerAlign">
          {dueDate ?? ""}
        </td>
      ),
      "SUPPLIER REFERENCE": (
        <TextOnHoverWhenAdjust
          key={"supplier ref"}
          text={reference_no}
          widthOfElement={columnHeaderWidth["supplier reference"] || 100}
        />
      ),
      ...billSourceDocumentListCells({ order, columnHeaderWidth }),
      TAX: (
        <td key="tax_excluded" className="rightAlign">
          {formattedAmount(tax_amount)}
        </td>
      ),
      "SUB TOTAL": (
        <td key="subtotal" className="rightAlign">
          {formattedAmount(sub_total)}
        </td>
      ),
      TOTAL: (
        <td key="total_amount" className="rightAlign">
          {`${currency?.symbol ?? ""} ${formattedAmount(total_amount)}`}
        </td>
      ),
      CURRENCY: <td key="bill_currency">{currencyField}</td>,
      STATUS: (
        <td key="bill_status" className="status">
          <Status statusName={order.status?.name ?? order.status} />
        </td>
      ),
    };
  },
  stockPack: ({ order, columnHeaderWidth, formattedAmount }) => {
    const [packDate, packDateWithTime] = showDateAndTime(order.pack_date);
    const packagingLabel = order.packaging_type
      ? [order.packaging_type.name, order.packaging_type.number].filter(Boolean).join(" — ")
      : "--";

    return {
      "PACK NO": (
        <td key="pack no" className="projectName">
          <Link to={`/stock_packs/${order.id}`}>
            <TextOnHoverWhenAdjust
              text={order.name}
              widthOfElement={columnHeaderWidth["pack no"] || 100}
            />
          </Link>
        </td>
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={order.status?.name} />
        </td>
      ),
      CUSTOMER: (
        <TextOnHoverWhenAdjust
          key="customer"
          text={order.customer?.display_name || "--"}
          widthOfElement={columnHeaderWidth["customer"] || 150}
        />
      ),
      "SUPPLY FACILITY": (
        <TextOnHoverWhenAdjust
          key="supply warehouse"
          text={order.warehouse?.name || "--"}
          widthOfElement={columnHeaderWidth["supply facility"] || 100}
        />
      ),
      "SHIPPING ADDRESS": (
        <TextOnHoverWhenAdjust
          key="shipping address"
          text={formatDocumentAddress(order.shipping_address) || "--"}
          widthOfElement={columnHeaderWidth["shipping address"] || 250}
        />
      ),
      "PACKAGING TYPE": (
        <TextOnHoverWhenAdjust
          key="packaging type"
          text={packagingLabel}
          widthOfElement={columnHeaderWidth["packaging type"] || 100}
        />
      ),
      "PACK DATE": (
        <td key="pack date" className="centerAlign" title={packDateWithTime}>
          {packDate || "--"}
        </td>
      ),
      "DANGEROUS GOODS": (
        <td key="dangerous goods" className="centerAlign">
          {order.is_dangerous_goods === true
            ? "Yes"
            : order.is_dangerous_goods === false
              ? "No"
              : "--"}
        </td>
      ),
      WEIGHT: (
        <TextOnHoverWhenAdjust
          key="weight"
          text={order.weight ? formattedAmount(order.weight) : "--"}
          widthOfElement={columnHeaderWidth["weight"] || 100}
          textAlign="right"
        />
      ),
      "WEIGHT UNIT": (
        <TextOnHoverWhenAdjust
          key="weight unit"
          text={order.weight_uom?.code || order.weight_uom?.name || order.weight_uom || "--"}
          widthOfElement={columnHeaderWidth["weight unit"] || 100}
        />
      ),
      LENGTH: (
        <TextOnHoverWhenAdjust
          key="length"
          text={order.length ? formattedAmount(order.length) : "--"}
          widthOfElement={columnHeaderWidth["length"] || 100}
          textAlign="right"
        />
      ),
      WIDTH: (
        <TextOnHoverWhenAdjust
          key="width"
          text={order.width ? formattedAmount(order.width) : "--"}
          widthOfElement={columnHeaderWidth["width"] || 100}
          textAlign="right"
        />
      ),
      HEIGHT: (
        <TextOnHoverWhenAdjust
          key="height"
          text={order.height ? formattedAmount(order.height) : "--"}
          widthOfElement={columnHeaderWidth["height"] || 100}
          textAlign="right"
        />
      ),
      CBM: (
        <TextOnHoverWhenAdjust
          key="cbm"
          text={order.cbm ? formattedAmount(order.cbm) : "--"}
          widthOfElement={columnHeaderWidth["cbm"] || 100}
          textAlign="right"
        />
      ),
      "DIMENSION UOM": (
        <TextOnHoverWhenAdjust
          key="dimension uom"
          text={order.dimension_uom?.code || order.dimension_uom?.name || "--"}
          widthOfElement={columnHeaderWidth["dimension uom"] || 100}
        />
      ),
    };
  },
  customerCreditNote: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      id,
      name,
      customer,
      customer_name,
      reference_no,
      credit_date,
      sale_invoice,
      sale_invoice_name,
      sub_total,
      tax_amount,
      total_amount,
      currency,
      status,
    } = order || {};

    const [creditDate, creditDateWithTime] = showDateAndTime(credit_date) || [];
    const customerLabel = customer_name || customer?.display_name || customer?.name || notAvailable;

    return {
      "CREDIT NO": (
        <td key="credit_no" className="projectName">
          {name ? <Link to={`/customer_credit_note/${id}`}>{name}</Link> : notAvailable}
        </td>
      ),
      CUSTOMER: (
        <TextOnHoverWhenAdjust
          key="customer"
          text={customerLabel}
          widthOfElement={columnHeaderWidth.customer || 120}
        />
      ),
      "CREDIT DATE": (
        <td key="credit_date" title={creditDateWithTime} className="centerAlign">
          {creditDate ?? ""}
        </td>
      ),
      REFERENCE: (
        <TextOnHoverWhenAdjust
          key="reference"
          text={reference_no}
          widthOfElement={columnHeaderWidth.reference || 120}
        />
      ),
      "SALE INVOICE": (
        <TextOnHoverWhenAdjust
          key="sale_invoice"
          text={sale_invoice_name || sale_invoice?.name}
          widthOfElement={columnHeaderWidth["sale invoice"] || 120}
        />
      ),
      "SUB TOTAL": (
        <td key="sub_total" className="rightAlign">
          {formattedAmount(sub_total)}
        </td>
      ),
      TAX: (
        <td key="tax_amount" className="rightAlign">
          {formattedAmount(tax_amount)}
        </td>
      ),
      TOTAL: (
        <td key="total_amount" className="rightAlign">
          {formattedAmount(total_amount)}
        </td>
      ),
      CURRENCY: (
        <TextOnHoverWhenAdjust
          key="currency"
          text={currency?.code || currency?.symbol}
          widthOfElement={columnHeaderWidth.currency || 80}
        />
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={status?.name ?? order.status} />
        </td>
      ),
    };
  },
  supplierCreditNote: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      id,
      name,
      supplier,
      reference_no,
      credit_date,
      total_amount,
      sub_total,
      currency,
      status,
    } = order || {};

    const [creditDate, creditDateWithTime] = showDateAndTime(credit_date) || [];

    const supplierName = supplier?.name ?? notAvailable;
    const currencyField = currency?.code ?? currency?.symbol ?? "";

    return {
      "CREDIT NO": (
        <td key="credit_no" className="projectName">
          {name ? <Link to={`/supplier_credit_note/${id}`}>{name}</Link> : notAvailable}
        </td>
      ),
      SUPPLIER: (
        <TextOnHoverWhenAdjust
          key={"supplier"}
          text={supplierName}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ),
      "CREDIT DATE": (
        <td key="credit_date" title={creditDateWithTime} className="centerAlign">
          {creditDate ?? ""}
        </td>
      ),
      "SUPPLIER REFERENCE": (
        <TextOnHoverWhenAdjust
          key={"supplier ref"}
          text={reference_no}
          widthOfElement={columnHeaderWidth["supplier reference"] || 100}
        />
      ),
      "TAX EXCLUDED": (
        <td key="tax_excluded" className="rightAlign">
          {formattedAmount(sub_total)}
        </td>
      ),
      TOTAL: (
        <td key="total" className="rightAlign">
          {`${currency?.symbol ?? ""} ${formattedAmount(total_amount)}`}
        </td>
      ),
      "BILL CURRENCY": <td key="bill_currency">{currencyField}</td>,
      "TOTAL IN CURRENCY": (
        <td key="total_in_currency" className="rightAlign">
          {currency?.symbol + " " + formattedAmount(total_amount)}
        </td>
      ),
      "PAYMENT STATUS": (
        <td key="payment_status" className="status">
          <Status statusName={status?.name ?? order.status} />
        </td>
      ),
    };
  },
  product: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      barcode,
      brand,
      categories,
      cbm,
      created_by,
      date_of_creation,
      dimension_uom,
      height,
      id,
      length,
      long_description,
      name,
      number,
      short_description,
      status,
      tags,
      tracking_method,
      type,
      uom,
      weight_uom,
      weight,
      width,
    } = order;

    const [creationDate, creationDateWithTime] = showDateAndTime(date_of_creation);

    return {
      NUMBER: (
        <TextOnHoverWhenAdjust
          key="number"
          text={number}
          widthOfElement={columnHeaderWidth["number"] || 100}
        />
      ),
      NAME: (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 125}
        />
      ),
      TYPE: (
        <TextOnHoverWhenAdjust
          key="type"
          text={type?.name}
          widthOfElement={columnHeaderWidth["type"] || 100}
        />
      ),
      CATEGORIES: (
        <td key="categories">
          <EllipsisMultiText
            text={categories?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["categories"] || 100}
          >
            {categories?.length
              ? categories?.map((item, i) => (
                  <Tag key={`cat-${id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      TAGS: (
        <td key="tags">
          <EllipsisMultiText
            text={tags?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["tags"] || 100}
          >
            {tags?.length
              ? tags?.map((item, i) => (
                  <Tag key={`cat-${id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      "TAX CLASSIFICATION": (
        <TextOnHoverWhenAdjust
          key="tax_classification"
          text={order.tax_classification?.name}
          widthOfElement={columnHeaderWidth["tax classification"] || 100}
        />
      ),
      "TRACKING METHOD": (
        <TextOnHoverWhenAdjust
          key="tracking_method"
          text={tracking_method?.name}
          widthOfElement={columnHeaderWidth["tracking method"] || 100}
        />
      ),
      BRAND: (
        <TextOnHoverWhenAdjust
          key="brand"
          text={brand}
          widthOfElement={columnHeaderWidth["brand"] || 100}
        />
      ),
      "SHORT DESCRIPTION": (
        <TextOnHoverWhenAdjust
          key="short_description"
          text={short_description}
          widthOfElement={columnHeaderWidth["short description"] || 130}
        />
      ),
      "LONG DESCRIPTION": (
        <TextOnHoverWhenAdjust
          key="long_description"
          text={long_description}
          widthOfElement={columnHeaderWidth["long description"] || 150}
        />
      ),
      BARCODE: (
        <TextOnHoverWhenAdjust
          key="barcode"
          text={barcode}
          widthOfElement={columnHeaderWidth["barcode"] || 100}
        />
      ),
      UOM: (
        <TextOnHoverWhenAdjust
          key="uom"
          text={uom}
          widthOfElement={columnHeaderWidth["uom"] || 100}
        />
      ),
      CBM: (
        <TextOnHoverWhenAdjust
          key=" cbm hovered"
          text={formattedAmount(cbm)}
          widthOfElement={columnHeaderWidth["cbm"] || 100}
          textAlign="right"
        />
      ),
      HEIGHT: (
        <TextOnHoverWhenAdjust
          key="height hovered"
          text={formattedAmount(height)}
          widthOfElement={columnHeaderWidth["height"] || 100}
          textAlign="right"
        />
      ),
      LENGTH: (
        <TextOnHoverWhenAdjust
          key=" length hovered"
          text={formattedAmount(length)}
          widthOfElement={columnHeaderWidth["length"] || 100}
          textAlign="right"
        />
      ),
      WIDTH: (
        <TextOnHoverWhenAdjust
          key="width  hovered"
          text={formattedAmount(width)}
          widthOfElement={columnHeaderWidth["width"] || 100}
          textAlign="right"
        />
      ),
      "DIMENSION UOM": (
        <TextOnHoverWhenAdjust
          key="dimension_uom"
          text={dimension_uom}
          widthOfElement={columnHeaderWidth["dimension uom"] || 100}
        />
      ),
      WEIGHT: (
        <TextOnHoverWhenAdjust
          key="weight  hovered"
          text={formattedAmount(weight)}
          widthOfElement={columnHeaderWidth["weight"] || 100}
          textAlign="right"
        />
      ),
      "WEIGHT UOM": (
        <TextOnHoverWhenAdjust
          key="weight_uom"
          text={weight_uom}
          widthOfElement={columnHeaderWidth["weight uom"] || 100}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 150}
        />
      ),
      "CREATE DATE": (
        <td key="date_of_creation" className="centerAlign" title={creationDateWithTime}>
          {creationDate ? creationDate : ""}
        </td>
      ),
      STATUS: (
        <td key="status" className="centerAlign">
          <StatusTag className={status?.name}>{status?.name}</StatusTag>
        </td>
      ),
    };
  },
  productCategory: ({ order, columnHeaderWidth }) => {
    const {
      name,
      short_description,
      long_description,
      date_of_creation,
      parent,
      created_by,
      is_active,
    } = order;

    const statusOfCategory = is_active ? "Active" : "Inactive";

    const [date, dateWithTime] = showDateAndTime(date_of_creation);
    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),

      "SHORT DESCRIPTION": (
        <TextOnHoverWhenAdjust
          key="short_description"
          text={short_description}
          widthOfElement={columnHeaderWidth["short description"] || 100}
        />
      ),
      "LONG DESCRIPTION": (
        <TextOnHoverWhenAdjust
          key="long_description"
          text={long_description}
          widthOfElement={columnHeaderWidth["long description"] || 100}
        />
      ),
      "CREATE DATE": (
        <td title={dateWithTime} key="date_of_creation" className="centerAlign">
          {date}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "PARENT CATEGORY": (
        <TextOnHoverWhenAdjust
          key="parent"
          text={parent?.name}
          widthOfElement={columnHeaderWidth["parent"] || 100}
        />
      ),
      "CATEGORY STATUS": (
        <TextOnHoverWhenAdjust
          key="category status"
          text={statusOfCategory}
          widthOfElement={columnHeaderWidth["category status"] || 100}
        />
      ),
    };
  },

  productTag: ({ order, columnHeaderWidth }) => {
    const { name, description, created_by, is_active } = order;

    const statusOfTag = is_active ? "Active" : "Inactive";
    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),

      "SHORT DESCRIPTION": (
        <TextOnHoverWhenAdjust
          key="description"
          text={description}
          widthOfElement={columnHeaderWidth["short description"] || 100}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "TAG STATUS": (
        <TextOnHoverWhenAdjust
          key="TAG status"
          text={statusOfTag}
          widthOfElement={columnHeaderWidth["tag status"] || 100}
        />
      ),
    };
  },
  equipmentType: ({ order, columnHeaderWidth }) => {
    const { name, code, category, is_default, created_by, date_of_creation } = order;

    const [date, dateWithTime] = showDateAndTime(date_of_creation);

    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      CODE: (
        <TextOnHoverWhenAdjust
          key="code"
          text={code}
          widthOfElement={columnHeaderWidth["code"] || 100}
        />
      ),
      CATEGORY: (
        <TextOnHoverWhenAdjust
          key="category"
          text={category?.name || category}
          widthOfElement={columnHeaderWidth["category"] || 100}
        />
      ),
      DEFAULT: (
        <td key="default" className="centerAlign">
          {is_default ? "Yes" : "No"}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "CREATE DATE": (
        <td title={dateWithTime} key="date_of_creation" className="centerAlign">
          {date}
        </td>
      ),
    };
  },
  equipment: ({ order, columnHeaderWidth }) => {
    const { equipment_type, number, is_active, created_by, date_of_creation } = order;

    const [date, dateWithTime] = showDateAndTime(date_of_creation);

    return {
      "EQUIPMENT TYPE": (
        <TextOnHoverWhenAdjust
          key="type"
          text={equipment_type?.name}
          widthOfElement={columnHeaderWidth["equipment type"] || 100}
        />
      ),
      NUMBER: (
        <TextOnHoverWhenAdjust
          key="number"
          text={number}
          widthOfElement={columnHeaderWidth["number"] || 100}
        />
      ),
      "EQUIPMENT STATUS": (
        <td key="status" className="centerAlign">
          {is_active ? "Active" : "Inactive"}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "CREATE DATE": (
        <td title={dateWithTime} key="date_of_creation" className="centerAlign">
          {date}
        </td>
      ),
    };
  },
  customerTag: ({ order, columnHeaderWidth }) => {
    const { name, description, created_by, is_active, date_of_creation } = order;
    const [date, dateWithTime] = showDateAndTime(date_of_creation);

    const statusOfTag = is_active ? "Active" : "Inactive";
    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),

      "SHORT DESCRIPTION": (
        <TextOnHoverWhenAdjust
          key="description"
          text={description}
          widthOfElement={columnHeaderWidth["short description"] || 100}
        />
      ),
      "TAG STATUS": (
        <TextOnHoverWhenAdjust
          key="tag status"
          text={statusOfTag}
          widthOfElement={columnHeaderWidth["tag status"] || 100}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "CREATE DATE": (
        <td title={dateWithTime} key="date_of_creation" className="centerAlign">
          {date}
        </td>
      ),
    };
  },
  productAlias: ({ order, columnHeaderWidth }) => {
    const { name, number, supplier, warehouse, product, created_by, date_of_creation } = order;

    const [creationDate, creationDateWithTime] = showDateAndTime(date_of_creation);

    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      NUMBER: (
        <TextOnHoverWhenAdjust
          key="number"
          text={number}
          widthOfElement={columnHeaderWidth["number"] || 100}
        />
      ),
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product"
          text={product?.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      FACILITY: warehouse ? (
        <TextOnHoverWhenAdjust
          key="warehouse"
          text={warehouse?.name}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ) : (
        <td key="whs">{notApplicable}</td>
      ),
      SUPPLIER: supplier ? (
        <TextOnHoverWhenAdjust
          key="supplier"
          text={supplier?.name}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ) : (
        <td key="whs">{notApplicable}</td>
      ),

      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "CREATE DATE": (
        <td key="date_of_creation" className="centerAlign" title={creationDateWithTime}>
          {creationDate ? creationDate : ""}
        </td>
      ),
    };
  },

  supplier: ({ order, columnHeaderWidth }) => {
    const {
      name,
      created_by,
      code,
      email,
      contact_number,
      address,
      physical_address,
      currency,
      notes,
      association_is_active,
      payment_term,
      is_co_manufacturer,
      xero_contact_id,
    } = order;

    const statusOfTag = association_is_active ? "Active" : "Inactive";
    const coManufacturerText = is_co_manufacturer ? "Yes" : "No";
    const xeroLinkedLabel = xero_contact_id ? "Linked" : "Not Linked";

    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      CODE: (
        <TextOnHoverWhenAdjust
          key="code"
          text={code}
          widthOfElement={columnHeaderWidth["code"] || 100}
        />
      ),
      "LINKED TO XERO": (
        <td key="linked_to_xero" className="centerAlign">
          <StatusTag className={xero_contact_id ? "Active" : "Inactive"}>
            {xeroLinkedLabel}
          </StatusTag>
        </td>
      ),
      "CO MANUFACTURER": (
        <TextOnHoverWhenAdjust
          key="co_manufacturer"
          text={coManufacturerText}
          widthOfElement={columnHeaderWidth["co manufacturer"] || 100}
        />
      ),
      EMAIL: (
        <TextOnHoverWhenAdjust
          key="email"
          text={email}
          widthOfElement={columnHeaderWidth["email"] || 100}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      CONTACT: (
        <TextOnHoverWhenAdjust
          key="CONTACT"
          text={contact_number}
          widthOfElement={columnHeaderWidth["contact"] || 100}
        />
      ),
      ADDRESS: (
        <TextOnHoverWhenAdjust
          key="address"
          text={address}
          widthOfElement={columnHeaderWidth["address"] || 100}
        />
      ),
      COUNTRY: (
        <TextOnHoverWhenAdjust
          key="country"
          text={getCountryDisplay(physical_address?.country)}
          widthOfElement={columnHeaderWidth["country"] || 100}
        />
      ),
      CURRENCY: (
        <TextOnHoverWhenAdjust
          key="currency"
          text={currency?.code}
          widthOfElement={columnHeaderWidth["currency"] || 100}
        />
      ),
      "PAYMENT TERMS": (
        <TextOnHoverWhenAdjust
          key="payment_terms"
          text={payment_term?.name}
          widthOfElement={columnHeaderWidth["payment terms"] || 100}
        />
      ),
      "SUPPLIER STATUS": (
        <TextOnHoverWhenAdjust
          key="status"
          text={statusOfTag}
          widthOfElement={columnHeaderWidth["supplier status"] || 100}
        />
      ),
      NOTES: (
        <TextOnHoverWhenAdjust
          key="notes"
          text={notes}
          widthOfElement={columnHeaderWidth["notes"] || 100}
        />
      ),
    };
  },
  customer: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      business_identifier,
      created_by,
      credit_hold,
      credit_limit_amount,
      currency,
      customer_code,
      date_of_creation,
      default_payment_term,
      default_sale_account,
      display_name,
      email,
      gst_number,
      legal_name,
      phone_number,
      status,
      type,
      sub_type,
      tags,
      sales_rep,
      xero_contact_id,
    } = order || {};

    const [creationDate, creationDateWithTime] = showDateAndTime(date_of_creation);
    const xeroLinkedLabel = xero_contact_id ? "Linked" : "Not Linked";

    return {
      CODE: (
        <TextOnHoverWhenAdjust
          key="code"
          text={customer_code}
          widthOfElement={columnHeaderWidth["code"] || 100}
        />
      ),
      "LINKED TO XERO": (
        <td key="linked_to_xero" className="centerAlign">
          <StatusTag className={xero_contact_id ? "Active" : "Inactive"}>
            {xeroLinkedLabel}
          </StatusTag>
        </td>
      ),
      "DISPLAY NAME": (
        <TextOnHoverWhenAdjust
          key="display name"
          text={display_name}
          widthOfElement={columnHeaderWidth["display name"] || 125}
        />
      ),
      "LEGAL NAME": (
        <TextOnHoverWhenAdjust
          key="legal name"
          text={legal_name}
          widthOfElement={columnHeaderWidth["legal name"] || 125}
        />
      ),
      TYPE: (
        <TextOnHoverWhenAdjust
          key="type"
          text={type?.name}
          widthOfElement={columnHeaderWidth["type"] || 100}
        />
      ),
      "COMPANY ID": (
        <TextOnHoverWhenAdjust
          key="company id"
          text={business_identifier}
          widthOfElement={columnHeaderWidth["company id"] || 100}
        />
      ),
      EMAIL: (
        <TextOnHoverWhenAdjust
          key="email"
          text={email}
          widthOfElement={columnHeaderWidth["email"] || 100}
        />
      ),
      "PHONE NO": (
        <TextOnHoverWhenAdjust
          key="phone no"
          text={phone_number}
          widthOfElement={columnHeaderWidth["phone no"] || 125}
        />
      ),
      CURRENCY: (
        <TextOnHoverWhenAdjust
          key="currency"
          text={currency?.code}
          widthOfElement={columnHeaderWidth["currency"] || 100}
        />
      ),
      "GST NO": (
        <TextOnHoverWhenAdjust
          key="gst no"
          text={gst_number}
          widthOfElement={columnHeaderWidth["gst no"] || 100}
        />
      ),
      "DEFAULT PAYMENT TERM": (
        <TextOnHoverWhenAdjust
          key="default payment term"
          text={default_payment_term?.name}
          widthOfElement={columnHeaderWidth["default payment term"] || 100}
        />
      ),
      "DEFAULT SALE ACCOUNT": (
        <TextOnHoverWhenAdjust
          key="default sale account"
          text={default_sale_account?.name}
          widthOfElement={columnHeaderWidth["default sale account"] || 100}
        />
      ),
      "CREDIT HOLD": (
        <TextOnHoverWhenAdjust
          key="credit hold"
          text={credit_hold ? "On" : "Off"}
          widthOfElement={columnHeaderWidth["credit hold"] || 100}
        />
      ),
      "CREDIT LIMIT AMOUNT": (
        <td key="credit limit amount" className="rightAlign">
          {formattedAmount(credit_limit_amount)}
        </td>
      ),
      STATUS: (
        <td key="status" className="centerAlign">
          <StatusTag className={status?.name}>{status?.name}</StatusTag>
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created by"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "CREATE DATE": (
        <td key="create date" className="centerAlign" title={creationDateWithTime}>
          {creationDate || "--"}
        </td>
      ),
      "SALES REP": (
        <TextOnHoverWhenAdjust
          key="sales_rep"
          text={sales_rep?.full_name || "--"}
          widthOfElement={columnHeaderWidth["sales rep"] || 125}
        />
      ),
      "SUB TYPE": (
        <TextOnHoverWhenAdjust
          key="sub_type"
          text={sub_type?.name || "--"}
          widthOfElement={columnHeaderWidth["sub type"] || 100}
        />
      ),
      TAGS: (
        <td key="tags">
          <EllipsisMultiText
            text={tags?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["tags"] || 100}
          >
            {tags?.length
              ? tags?.map((item, i) => (
                  <Tag key={`tags-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      "DEFAULT FACILITY": (
        <TextOnHoverWhenAdjust
          key="default_warehouse"
          text={order.default_warehouse?.name || "--"}
          widthOfElement={columnHeaderWidth["default facility"] || 125}
        />
      ),
      "DEFAULT TAX RATE": (
        <TextOnHoverWhenAdjust
          key="default_tax_rate"
          text={order.default_tax_rate?.name || "--"}
          widthOfElement={columnHeaderWidth["default tax rate"] || 125}
        />
      ),
    };
  },
  warehouse: ({ order, columnHeaderWidth }) => {
    const {
      name,
      short_description,
      long_description,
      created_by,
      local_transit_time_buffer_air,
      local_transit_time_buffer_ocean,
      address,
      code,
      country,
      physical_address,
      google_maps_address,
      state,
      site,
      types,
    } = order;

    const airTransitTimeInDays = timeInDays(local_transit_time_buffer_air || 0);
    const oceanTransitTimeInDays = timeInDays(local_transit_time_buffer_ocean || 0);
    const countryLabel =
      getCountryDisplay(physical_address?.country) || getCountryDisplay(country) || "--";
    const typeItems = (Array.isArray(types) ? types : [])
      .map(item => {
        const id = item?.id || item?.name || item;
        return id ? { id, name: warehouseTypeLabel(id) } : null;
      })
      .filter(Boolean);

    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),

      "SHORT DESCRIPTION": (
        <TextOnHoverWhenAdjust
          key="short_description"
          text={short_description}
          widthOfElement={columnHeaderWidth["short description"] || 100}
        />
      ),
      "LONG DESCRIPTION": (
        <TextOnHoverWhenAdjust
          key="long_description"
          text={long_description}
          widthOfElement={columnHeaderWidth["long description"] || 100}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "GOOGLE MAP ADDRESS": (
        <TextOnHoverWhenAdjust
          key="GOOGLE MAP ADDRESS"
          text={google_maps_address}
          widthOfElement={columnHeaderWidth["google map address"] || 100}
        />
      ),
      ADDRESS: (
        <TextOnHoverWhenAdjust
          key="address"
          text={physical_address?.formatted_address || address}
          widthOfElement={columnHeaderWidth["address"] || 100}
        />
      ),
      CODE: (
        <TextOnHoverWhenAdjust
          key="code"
          text={code}
          widthOfElement={columnHeaderWidth["code"] || 100}
        />
      ),
      SITE: (
        <TextOnHoverWhenAdjust
          key="site"
          text={site?.name || "--"}
          widthOfElement={columnHeaderWidth["site"] || 100}
        />
      ),
      TYPES: (
        <td key="types">
          <EllipsisMultiText
            text={typeItems?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["types"] || 140}
          >
            {typeItems?.length
              ? typeItems?.map((item, i) => (
                  <Tag key={`type-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      STATE: (
        <TextOnHoverWhenAdjust
          key="state"
          text={physical_address?.state_name || physical_address?.state_code || state}
          widthOfElement={columnHeaderWidth["state"] || 100}
        />
      ),
      COUNTRY: (
        <TextOnHoverWhenAdjust
          key="country"
          text={countryLabel}
          widthOfElement={columnHeaderWidth["country"] || 100}
        />
      ),
      "TRANSIT AIR": (
        <TextOnHoverWhenAdjust
          key="ait"
          text={airTransitTimeInDays}
          widthOfElement={columnHeaderWidth["transit air"] || 100}
        />
      ),
      "TRANSIT OCEAN": (
        <TextOnHoverWhenAdjust
          key="ocean"
          text={oceanTransitTimeInDays}
          widthOfElement={columnHeaderWidth["transit ocean"] || 100}
        />
      ),
    };
  },
  unitOfMeasure: ({ order, columnHeaderWidth }) => {
    const { code, name, category, is_default } = order;
    return {
      CODE: (
        <TextOnHoverWhenAdjust
          key="code"
          text={code}
          widthOfElement={columnHeaderWidth["code"] || 100}
        />
      ),
      NAME: (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 125}
        />
      ),
      CATEGORY: (
        <TextOnHoverWhenAdjust
          key="category"
          text={category?.name}
          widthOfElement={columnHeaderWidth["category"] || 100}
        />
      ),
      "SYSTEM DEFAULT": (
        <TextOnHoverWhenAdjust
          key="is_default"
          text={is_default ? "Standard" : "Custom"}
          widthOfElement={columnHeaderWidth["system default"] || 100}
        />
      ),
    };
  },

  pack: ({ order, columnHeaderWidth, formattedAmount }) => {
    const [createDate, createDateWithTime] = showDateAndTime(order.date_of_creation);
    const scope = [
      order.scope_purchase && "Purchase",
      order.scope_sales && "Sales",
      order.scope_inventory && "Inventory",
      order.scope_production && "Production",
    ]
      .filter(Boolean)
      .map(item => ({ id: item, name: item }));
    return {
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product hovered"
          text={order.product?.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      SUPPLIER: (
        <TextOnHoverWhenAdjust
          key="supplier hovered"
          text={order?.supplier?.name}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ),
      "FROM FACILITY": (
        <TextOnHoverWhenAdjust
          key="from whs hovered"
          text={order?.from_warehouse?.name}
          widthOfElement={columnHeaderWidth["from facility"] || 100}
        />
      ),
      "TO FACILITY": (
        <TextOnHoverWhenAdjust
          key="to whs hovered"
          text={order?.to_warehouse?.name}
          widthOfElement={columnHeaderWidth["to facility"] || 100}
        />
      ),
      UOM: (
        <TextOnHoverWhenAdjust
          key="uom hovered"
          text={order?.uom?.name}
          widthOfElement={columnHeaderWidth["uom"] || 100}
        />
      ),
      "QTY/PACK": (
        <TextOnHoverWhenAdjust
          key="qty hovered"
          text={formattedAmount(order?.qty_per_pack)}
          widthOfElement={columnHeaderWidth["qty/pack"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "CREATE DATE": (
        <td key="eta" className="centerAlign" title={createDateWithTime}>
          {createDate ? <> {createDate}</> : "--"}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key=" hovered"
          text={order?.created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      SCOPE: (
        <td key="scope">
          <EllipsisMultiText
            text={scope?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["scope"] || 100}
          >
            {scope?.length
              ? scope?.map((item, i) => (
                  <Tag key={`scope-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      WEIGHT: (
        <TextOnHoverWhenAdjust
          key="weight hovered"
          text={formattedAmount(order?.weight)}
          widthOfElement={columnHeaderWidth["weight"] || 80}
          textAlign="right"
        />
      ),
      "WEIGHT UNITS": (
        <TextOnHoverWhenAdjust
          key="weight units hovered"
          text={order?.weight_uom?.name || "--"}
          widthOfElement={columnHeaderWidth["weight units"] || 100}
        />
      ),
      LENGTH: (
        <TextOnHoverWhenAdjust
          key="length hovered"
          text={formattedAmount(order?.length)}
          widthOfElement={columnHeaderWidth["length"] || 80}
          textAlign="right"
        />
      ),
      WIDTH: (
        <TextOnHoverWhenAdjust
          key="width hovered"
          text={formattedAmount(order?.width)}
          widthOfElement={columnHeaderWidth["width"] || 80}
          textAlign="right"
        />
      ),
      HEIGHT: (
        <TextOnHoverWhenAdjust
          key="height hovered"
          text={formattedAmount(order?.height)}
          widthOfElement={columnHeaderWidth["height"] || 80}
          textAlign="right"
        />
      ),
      "DIMENSION UNITS": (
        <TextOnHoverWhenAdjust
          key="dimension units hovered"
          text={order?.dimension_uom?.name || "--"}
          widthOfElement={columnHeaderWidth["dimension units"] || 100}
        />
      ),
    };
  },
  warehouseCostingRange: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      from_qty,
      to_qty,
      created_by,
      price,
      date_of_creation,
      warehouse_costing: { product, from_warehouse, to_warehouse, supplier },
    } = order;
    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation);
    return {
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product hovered"
          text={product?.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),

      SUPPLIER: supplier?.code ? (
        <TextOnHoverWhenAdjust
          key="supplier hovered"
          text={supplier?.code}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ) : (
        <td key="suppllier" className="wareHouse">
          {supplier ? notCodeAvailable : notApplicable}
        </td>
      ),
      "FROM FACILITY": from_warehouse?.code ? (
        <TextOnHoverWhenAdjust
          key="from whs hovered"
          text={from_warehouse.code}
          widthOfElement={columnHeaderWidth["from facility"] || 100}
        />
      ) : (
        <td key="from warehouse" className="wareHouse">
          {from_warehouse ? notCodeAvailable : notApplicable}
        </td>
      ),

      "TO FACILITY": to_warehouse?.code ? (
        <TextOnHoverWhenAdjust
          key="to whs hovered"
          text={to_warehouse?.code}
          widthOfElement={columnHeaderWidth["to facility"] || 100}
        />
      ) : (
        <td key="to warehouse" className="wareHouse">
          {to_warehouse ? notCodeAvailable : notApplicable}
        </td>
      ),
      "FROM QUANTITY": (
        <TextOnHoverWhenAdjust
          key="from qty hovered"
          text={formattedAmount(from_qty)}
          widthOfElement={columnHeaderWidth["from quantity"] || 100}
          textAlign="right"
        />
      ),
      "TO QUANTITY": (
        <TextOnHoverWhenAdjust
          key=" to qty hovered"
          text={formattedAmount(to_qty)}
          widthOfElement={columnHeaderWidth["to quantity"] || 100}
          textAlign="right"
        />
      ),
      PRICE: (
        <TextOnHoverWhenAdjust
          key=" price hovered"
          text={formattedAmount(price)}
          widthOfElement={columnHeaderWidth["price"] || 100}
          textAlign="right"
        />
      ),
      "CREATE DATE": (
        <td key="eta" className="centerAlign" title={createDateWithTime}>
          {createDate ? <> {createDate}</> : "--"}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key=" hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
    };
  },
  stockZone: ({ order, columnHeaderWidth }) => {
    const { name, warehouse, type, created_by, date_of_creation } = order;
    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation);
    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name hovered"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="facility hovered"
          text={warehouse?.name}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),
      TYPE: (
        <TextOnHoverWhenAdjust
          key="TYPE hovered"
          text={stockZoneTypeLabel(type?.id || type?.name || type)}
          widthOfElement={columnHeaderWidth["type"] || 100}
        />
      ),
      "CREATE DATE": (
        <td key="eta" className="centerAlign" title={createDateWithTime}>
          {createDate ? <> {createDate}</> : "--"}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key=" hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
    };
  },
  forecast: ({ order, columnHeaderWidth, formattedAmount }) => {
    const { product, warehouse, from_date, to_date, created_by, qty } = order;
    const [createDate, createDateWithTime] = showDateAndTime(order.date_of_creation);
    return {
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product hovered"
          text={product?.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="facility hovered"
          text={warehouse?.name}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),
      FROM: (
        <td key="eta" className="centerAlign">
          {from_date ? <> {from_date}</> : "--"}
        </td>
      ),
      TO: (
        <td key="to date" className="centerAlign">
          {to_date ? <> {to_date}</> : "--"}
        </td>
      ),
      QUANTITY: (
        <TextOnHoverWhenAdjust
          key="qty hovered"
          text={formattedAmount(qty)}
          widthOfElement={columnHeaderWidth["quantity"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "CREATE DATE": (
        <td key="eta" className="centerAlign" title={createDateWithTime}>
          {createDate ? <> {createDate}</> : "--"}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key=" hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
    };
  },
  warehouseCosting: ({ order, columnHeaderWidth, formattedAmount }) => {
    const { product, from_warehouse, to_warehouse, supplier, date_of_creation, created_by } = order;
    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation);

    return {
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product hovered"
          text={product?.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),

      SUPPLIER: supplier?.name ? (
        <TextOnHoverWhenAdjust
          key="supplier hovered"
          text={supplier.name}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ) : (
        <td key="suppllier" className="wareHouse">
          {supplier ? notNameAvailable : notApplicable}
        </td>
      ),
      "FROM FACILITY": from_warehouse?.name ? (
        <TextOnHoverWhenAdjust
          key="from whs hovered"
          text={from_warehouse.name}
          widthOfElement={columnHeaderWidth["from facility"] || 100}
        />
      ) : (
        <td key="from warehouse" className="wareHouse">
          {from_warehouse ? notNameAvailable : notApplicable}
        </td>
      ),

      "TO FACILITY": to_warehouse?.name ? (
        <TextOnHoverWhenAdjust
          key="to whs hovered"
          text={to_warehouse.name}
          widthOfElement={columnHeaderWidth["to facility"] || 100}
        />
      ) : (
        <td key="to warehouse" className="wareHouse">
          {to_warehouse ? notNameAvailable : notApplicable}
        </td>
      ),
      ...facilityCostingBandListCells({ order, columnHeaderWidth, formattedAmount }),
      "CREATE DATE": (
        <td key="eta" className="centerAlign" title={createDateWithTime}>
          {createDate ? <> {createDate}</> : "--"}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key=" hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
    };
  },

  stockLocation: ({ order, columnHeaderWidth }) => {
    const { name, code, stock_zone, created_by, date_of_creation } = order;
    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation);
    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name hovered"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      CODE: (
        <TextOnHoverWhenAdjust
          key="facility hovered"
          text={code}
          widthOfElement={columnHeaderWidth["code"] || 100}
        />
      ),
      "STOCK ZONE": (
        <TextOnHoverWhenAdjust
          key="stock zone hovered"
          text={stock_zone?.name}
          widthOfElement={columnHeaderWidth["stock zone"] || 100}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="facility hovered"
          text={stock_zone?.warehouse?.name}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),
      "CREATE DATE": (
        <td key="eta" className="centerAlign" title={createDateWithTime}>
          {createDate ? <> {createDate}</> : "--"}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key=" hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
    };
  },
  productPlan: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      product,
      supplier,
      from_warehouse,
      to_warehouse,
      min_ord_qty,
      ord_mul,
      plan_horizon_days,
      planning_policy,
      production_lead_time_days,
      ageing_days,
      rop_days,
      rop_method,
      rop_qty,
      safety_stock_qty,
      safety_stock_days,
      safety_stock_method,
      total_lead_time_days,
      transit_lead_time_days,
      acquisition_code,
      is_default,
      created_by,
      pick_location,
      replenishment_calculation_method,
      replenishment_qty,
      replenishment_trigger_qty,
      max_stock_qty,
    } = order;

    return {
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product hovered"
          text={product?.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      SUPPLIER: (
        <TextOnHoverWhenAdjust
          key="supplier hovered"
          text={supplier?.name}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ),
      "FROM FACILITY": (
        <TextOnHoverWhenAdjust
          key="from whs hovered"
          text={from_warehouse?.name}
          widthOfElement={columnHeaderWidth["from facility"] || 100}
        />
      ),
      "TO FACILITY": (
        <TextOnHoverWhenAdjust
          key="to whs hovered"
          text={to_warehouse?.name}
          widthOfElement={columnHeaderWidth["to facility"] || 100}
        />
      ),
      "PICK LOCATION": (
        <TextOnHoverWhenAdjust
          key="pick_location"
          text={pick_location?.name ?? "--"}
          widthOfElement={columnHeaderWidth["pick location"] || 100}
        />
      ),
      "REPLENISHMENT METHOD": (
        <TextOnHoverWhenAdjust
          key="replenishment_method"
          text={replenishment_calculation_method?.name ?? "--"}
          widthOfElement={columnHeaderWidth["replenishment method"] || 100}
        />
      ),
      "REPLENISHMENT QUANTITY": (
        <TextOnHoverWhenAdjust
          key="replenishment_qty hovered"
          text={formattedAmount(replenishment_qty)}
          widthOfElement={
            columnHeaderWidth["replenishment quantity"] || COLUMN_CONSTRAINTS.MIN_WIDTH
          }
          textAlign="right"
        />
      ),
      "TRIGGER QUANTITY": (
        <TextOnHoverWhenAdjust
          key="trigger_qty hovered"
          text={formattedAmount(replenishment_trigger_qty)}
          widthOfElement={columnHeaderWidth["trigger quantity"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "MAX STOCK QUANTITY": (
        <TextOnHoverWhenAdjust
          key="max_stock_qty hovered"
          text={formattedAmount(max_stock_qty)}
          widthOfElement={columnHeaderWidth["max stock quantity"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),

      "MIN ORDER QTY": (
        <TextOnHoverWhenAdjust
          key="minordqty hovered"
          text={formattedAmount(min_ord_qty)}
          widthOfElement={columnHeaderWidth["min order qty"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "ORDER MULTIPLE": (
        <TextOnHoverWhenAdjust
          key="ord multiple hovered"
          text={formattedAmount(ord_mul)}
          widthOfElement={columnHeaderWidth["order multiple"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "PLAN HORIZON DAYS": (
        <TextOnHoverWhenAdjust
          key="plan_horizon_days"
          text={plan_horizon_days ?? "--"}
          widthOfElement={columnHeaderWidth["plan horizon days"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "PLANNING POLICY": (
        <TextOnHoverWhenAdjust
          key="planning_policy"
          text={planning_policy?.name ?? "--"}
          widthOfElement={columnHeaderWidth["planning policy"] || 100}
        />
      ),
      "PRODUCTION LEAD TIME DAYS": (
        <TextOnHoverWhenAdjust
          key="production_lead_time_days"
          text={production_lead_time_days ?? "--"}
          widthOfElement={
            columnHeaderWidth["production lead time days"] || COLUMN_CONSTRAINTS.MIN_WIDTH
          }
          textAlign="right"
        />
      ),
      "AGEING DAYS": (
        <TextOnHoverWhenAdjust
          key="ageing_days"
          text={ageing_days ?? "--"}
          widthOfElement={columnHeaderWidth["ageing days"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "REORDER POINT DAYS": (
        <TextOnHoverWhenAdjust
          key="rop_days"
          text={rop_days ?? "--"}
          widthOfElement={columnHeaderWidth["reorder point days"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "REORDER POINT METHOD": (
        <TextOnHoverWhenAdjust
          key="rop_method"
          text={rop_method?.name ?? "--"}
          widthOfElement={columnHeaderWidth["reorder point method"] || 100}
        />
      ),
      "REORDER POINT QTY": (
        <TextOnHoverWhenAdjust
          key="reorder pt qty hovered"
          text={formattedAmount(rop_qty)}
          widthOfElement={columnHeaderWidth["reorder point qty"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "SAFETY STOCK QTY": (
        <TextOnHoverWhenAdjust
          key="minStock hovered"
          text={formattedAmount(safety_stock_qty)}
          widthOfElement={columnHeaderWidth["safety stock qty"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "SAFETY STOCK DAYS": (
        <TextOnHoverWhenAdjust
          key="safety_stock_days"
          text={safety_stock_days ?? "--"}
          widthOfElement={columnHeaderWidth["safety stock days"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "SAFETY STOCK METHOD": (
        <TextOnHoverWhenAdjust
          key="safety_stock_mehod"
          text={safety_stock_method?.name ?? "--"}
          widthOfElement={columnHeaderWidth["safety stock method"] || 100}
        />
      ),
      "TOTAL LEAD TIME DAYS": (
        <TextOnHoverWhenAdjust
          key="total_lead_time_days"
          text={total_lead_time_days ?? "--"}
          widthOfElement={columnHeaderWidth["total lead time days"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "TRANSIT LEAD TIME DAYS": (
        <TextOnHoverWhenAdjust
          key="transit_lead_time_days"
          text={transit_lead_time_days ?? "--"}
          widthOfElement={
            columnHeaderWidth["transit lead time days"] || COLUMN_CONSTRAINTS.MIN_WIDTH
          }
          textAlign="right"
        />
      ),
      "ACQUISITION TYPE": (
        <TextOnHoverWhenAdjust
          key="acquisition_code"
          text={acquisition_code?.name ?? acquisition_code ?? "--"}
          widthOfElement={columnHeaderWidth["acquisition type"] || 100}
        />
      ),
      DEFAULT: (
        <TextOnHoverWhenAdjust
          key="is_default"
          text={is_default ? "Yes" : "No"}
          widthOfElement={columnHeaderWidth["default"] || 80}
        />
      ),

      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key=" hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "ACTIVITY LOG": (
        <ActivityLogCell
          productPlanId={order.id}
          columnWidth={columnHeaderWidth["activity log"] || 100}
        />
      ),
    };
  },

  transactionReason: ({ order, columnHeaderWidth }) => {
    const { reason, code, impact, created_by, date_of_creation } = order;
    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation);

    return {
      REASON: (
        <TextOnHoverWhenAdjust
          key="reason hovered"
          text={reason}
          widthOfElement={columnHeaderWidth["reason"] || 100}
        />
      ),
      CODE: (
        <TextOnHoverWhenAdjust
          key="code hovered"
          text={code}
          widthOfElement={columnHeaderWidth["code"] || 100}
        />
      ),
      IMPACT: (
        <TextOnHoverWhenAdjust
          key="impact hovered"
          text={impact?.name}
          widthOfElement={columnHeaderWidth["impact"] || 100}
        />
      ),
      "CREATE DATE": (
        <td key="eta" className="centerAlign" title={createDateWithTime}>
          {createDate ? <> {createDate}</> : "--"}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key=" hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
    };
  },

  standardCost: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      product,
      warehouse,
      unit_price,
      landed_cost,
      landed_cost_type,
      total_unit_price,
      created_by,
      date_of_creation,
    } = order;
    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation);

    const landedCost =
      landed_cost_type?.id === "%" ? unit_price * (landed_cost / 100) : landed_cost;

    return {
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product hovered"
          text={product?.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="facility hovered"
          text={warehouse?.name}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),

      "UNIT COST": (
        <TextOnHoverWhenAdjust
          key="unit price hovered"
          text={formattedAmount(unit_price)}
          widthOfElement={columnHeaderWidth["unit cost"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "OTHER COSTS TYPE": (
        <TextOnHoverWhenAdjust
          key="landed cost type  hovered"
          text={landed_cost_type?.name}
          widthOfElement={columnHeaderWidth["other costs type"] || 100}
        />
      ),
      "OTHER COSTS": (
        <TextOnHoverWhenAdjust
          key="landed cost  hovered"
          text={formattedAmount(landedCost || 0)}
          widthOfElement={columnHeaderWidth["other costs"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "TOTAL LANDED COSTS": (
        <TextOnHoverWhenAdjust
          key=" total_unit_price hovered"
          text={formattedAmount(total_unit_price)}
          widthOfElement={columnHeaderWidth["total landed costs"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "CREATE DATE": (
        <td key="eta" className="centerAlign" title={createDateWithTime}>
          {createDate ? <> {createDate}</> : "--"}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key=" hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
    };
  },
  site: ({ order, columnHeaderWidth }) => {
    const { code, name, timezone, is_active, created_by, date_of_creation, physical_address } =
      order;
    const activeStatus = is_active ? "Yes" : "No";
    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation);
    return {
      CODE: (
        <TextOnHoverWhenAdjust
          key="site code hovered"
          text={code}
          widthOfElement={columnHeaderWidth["code"] || 100}
        />
      ),
      NAME: (
        <TextOnHoverWhenAdjust
          key="site name hovered"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      "TIME ZONE": (
        <TextOnHoverWhenAdjust
          key="site timezone hovered"
          text={typeof timezone === "object" ? timezone?.name : timezone}
          widthOfElement={columnHeaderWidth["time zone"] || 100}
        />
      ),
      ACTIVE: (
        <TextOnHoverWhenAdjust
          key="active"
          text={activeStatus}
          widthOfElement={columnHeaderWidth["active"] || 100}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="site created by hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "CREATE DATE": (
        <TextOnHoverWhenAdjust
          key="site create date hovered"
          text={createDate}
          textToHover={createDateWithTime}
          widthOfElement={columnHeaderWidth["create date"] || 100}
        />
      ),
      ADDRESS: (
        <TextOnHoverWhenAdjust
          key="site address hovered"
          text={formatAddressV2(physical_address)}
          widthOfElement={columnHeaderWidth["address"] || 100}
        />
      ),
    };
  },

  workCenter: ({ order, columnHeaderWidth }) => {
    const { code, name, type, location, warehouse, zone, created_by, date_of_creation, is_active } =
      order;
    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation);

    const activeStatus = is_active ? "Yes" : "No";
    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="workCenter name hovered"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      TYPE: (
        <TextOnHoverWhenAdjust
          key="workCenter type hovered"
          text={type?.name || type?.id || type}
          widthOfElement={columnHeaderWidth["type"] || 100}
        />
      ),
      CODE: (
        <TextOnHoverWhenAdjust
          key="workCenter code hovered"
          text={code}
          widthOfElement={columnHeaderWidth["code"] || 100}
        />
      ),
      LOCATION: (
        <TextOnHoverWhenAdjust
          key="workCenter location hovered"
          text={location?.name || location?.code}
          widthOfElement={columnHeaderWidth["location"] || 100}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="workCenter facility hovered"
          text={warehouse?.name}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),
      "STOCK ZONE": (
        <TextOnHoverWhenAdjust
          key="workCenter zone hovered"
          text={zone?.name}
          widthOfElement={columnHeaderWidth["stock zone"] || 100}
        />
      ),
      ACTIVE: (
        <TextOnHoverWhenAdjust
          key="active"
          text={activeStatus}
          widthOfElement={columnHeaderWidth["active"] || 100}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="workCenter created by hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "CREATE DATE": (
        <TextOnHoverWhenAdjust
          key="workCenter create date hovered"
          text={createDate}
          textToHover={createDateWithTime}
          widthOfElement={columnHeaderWidth["create date"] || 100}
        />
      ),
    };
  },

  users: ({ order, columnHeaderWidth }) => {
    const { full_name, email, groups, company, supplier, last_login, date_of_creation } =
      order || {};
    const roleNames = (groups || [])
      .map(g => g?.name)
      .filter(Boolean)
      .join(", ");
    const [joinedDate, joinedDateWithTime] = showDateAndTime(date_of_creation);
    const [lastLoginDate, lastLoginWithTime] = showDateAndTime(last_login);

    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="users name"
          text={full_name || email || "--"}
          widthOfElement={columnHeaderWidth["name"] || 160}
        />
      ),
      EMAIL: (
        <TextOnHoverWhenAdjust
          key="users email"
          text={email}
          widthOfElement={columnHeaderWidth["email"] || 180}
        />
      ),
      ROLE: (
        <TextOnHoverWhenAdjust
          key="users role"
          text={roleNames || "--"}
          widthOfElement={columnHeaderWidth["role"] || 120}
        />
      ),
      COMPANY: (
        <TextOnHoverWhenAdjust
          key="users company"
          text={company?.name || "--"}
          widthOfElement={columnHeaderWidth["company"] || 140}
        />
      ),
      PARTNER: (
        <TextOnHoverWhenAdjust
          key="users partner"
          text={supplier?.name || "--"}
          widthOfElement={columnHeaderWidth["partner"] || 140}
        />
      ),
      "LAST LOGIN": (
        <TextOnHoverWhenAdjust
          key="users last login"
          text={lastLoginDate || "--"}
          textToHover={lastLoginWithTime}
          widthOfElement={columnHeaderWidth["last login"] || 120}
        />
      ),
      JOINED: (
        <TextOnHoverWhenAdjust
          key="users joined"
          text={joinedDate || "--"}
          textToHover={joinedDateWithTime}
          widthOfElement={columnHeaderWidth["joined"] || 120}
        />
      ),
      STATUS: <UsersStatusCell key="users status" order={order} />,
    };
  },

  people: ({ order, columnHeaderWidth }) => {
    const { full_name, role, email, phone, is_active, created_by, date_of_creation } = order;
    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation);
    const peopleStatus = is_active ? "Active" : "Inactive";
    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="people name hovered"
          text={full_name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      ROLE: (
        <TextOnHoverWhenAdjust
          key="people role hovered"
          text={role?.name || role?.id || role}
          widthOfElement={columnHeaderWidth["role"] || 100}
        />
      ),
      EMAIL: (
        <TextOnHoverWhenAdjust
          key="people email hovered"
          text={email}
          widthOfElement={columnHeaderWidth["email"] || 100}
        />
      ),
      PHONE: (
        <TextOnHoverWhenAdjust
          key="people phone hovered"
          text={phone}
          widthOfElement={columnHeaderWidth["phone"] || 100}
        />
      ),
      "PEOPLE STATUS": (
        <TextOnHoverWhenAdjust
          key="people status hovered"
          text={peopleStatus}
          widthOfElement={columnHeaderWidth["people status"] || 100}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="people created by hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "CREATE DATE": (
        <TextOnHoverWhenAdjust
          key="people create date hovered"
          text={createDate}
          textToHover={createDateWithTime}
          widthOfElement={columnHeaderWidth["create date"] || 100}
        />
      ),
    };
  },

  boms: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      name,
      id,
      type,
      product,
      output_qty,
      cost_per_unit,
      total_cost,
      auto_assembly,
      status,
      labour_cost,
      created_by,
      date_of_creation,
      supplier,
      warehouse,
    } = order;

    const inventoryConsumption = auto_assembly ? "Auto" : "Manual";

    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation);

    return {
      "BOM NO": (
        <td key="pack no" className="projectName">
          <Link to={`/bom/${id}`}>
            <TextOnHoverWhenAdjust
              key="bom no hovered"
              text={name}
              widthOfElement={columnHeaderWidth["bom no"] || 100}
            />
          </Link>
        </td>
      ),
      TYPE: (
        <TextOnHoverWhenAdjust
          key="bom type hovered"
          text={type?.name || "-"}
          widthOfElement={columnHeaderWidth["type"] || 100}
        />
      ),
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="bom product hovered"
          text={product?.number || "-"}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      "PRODUCT NAME": (
        <TextOnHoverWhenAdjust
          key="bom product name hovered"
          text={product?.name || "-"}
          widthOfElement={columnHeaderWidth["product name"] || 100}
        />
      ),
      "OUTPUT QUANTITY": (
        <TextOnHoverWhenAdjust
          key="bom output qty hovered"
          textAlign="right"
          text={formattedAmount(output_qty, null, null, false)}
          widthOfElement={columnHeaderWidth["output quantity"] || 100}
        />
      ),
      "COST P/UNIT": (
        <TextOnHoverWhenAdjust
          key="bom cost p/unit hovered"
          textAlign="right"
          text={formattedAmount(cost_per_unit, null, null, false)}
          widthOfElement={columnHeaderWidth["cost p/unit"] || 100}
        />
      ),
      "LABOUR COST": (
        <TextOnHoverWhenAdjust
          key="bom labour cost hovered"
          textAlign="right"
          text={formattedAmount(labour_cost, null, null, false)}
          widthOfElement={columnHeaderWidth["labour cost"] || 100}
        />
      ),
      "TOTAL COST": (
        <TextOnHoverWhenAdjust
          key="bom total cost hovered"
          textAlign="right"
          text={formattedAmount(total_cost, null, null, false)}
          widthOfElement={columnHeaderWidth["total cost"] || 100}
        />
      ),
      "INVENTORY CONSUMPTION": (
        <TextOnHoverWhenAdjust
          key="bom inventory consumption hovered"
          text={inventoryConsumption}
          widthOfElement={columnHeaderWidth["inventory consumption"] || 100}
        />
      ),
      SUPPLIER: (
        <TextOnHoverWhenAdjust
          key="bom supplier hovered"
          text={supplier?.name || "-"}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="bom warehouse hovered"
          text={warehouse?.name || "-"}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="bom created by hovered"
          text={created_by?.name || created_by?.full_name || created_by || "-"}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),

      "CREATE DATE": (
        <td key="eta" className="centerAlign" title={createDateWithTime}>
          {createDate ? <> {createDate}</> : "--"}
        </td>
      ),
      STATUS: (
        <td key="status" className="centerAlign">
          <StatusTag className={status?.name}>{status?.name}</StatusTag>
        </td>
      ),
    };
  },
  manufacturingOrder: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      id,
      name,
      product,
      bom,
      bom_version,
      status,
      production_qty,
      uom,
      planned_start,
      required_by,
      estimated_completion,
      warehouse,
    } = order;

    const plannedStartDate = formatProductionClock(planned_start);
    const requiredByDate = formatProductionClock(required_by);
    const estCompletionDate = formatProductionClock(estimated_completion);

    return {
      "MO NO": (
        <td key="mo no" className="projectName">
          <Link to={`/manufacturing-order/${id}`}>
            <TextOnHoverWhenAdjust
              key="mo no hovered"
              text={name || "--"}
              widthOfElement={columnHeaderWidth["mo no"] || 100}
            />
          </Link>
        </td>
      ),
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="mo product hovered"
          text={product?.number || product?.name || product || "-"}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      "BOM VERSION": (
        <TextOnHoverWhenAdjust
          key="mo bom hovered"
          text={bom_version?.name || bom?.name || bom || "-"}
          widthOfElement={columnHeaderWidth["bom version"] || 100}
        />
      ),
      STATUS: (
        <td key="status" className="centerAlign">
          <StatusTag className={status?.name || status}>{status?.name || status}</StatusTag>
        </td>
      ),
      "PRODUCTION QUANTITY": (
        <TextOnHoverWhenAdjust
          key="mo qty hovered"
          textAlign="right"
          text={formattedAmount(production_qty, null, null, false)}
          widthOfElement={columnHeaderWidth["production quantity"] || 100}
        />
      ),
      UOM: (
        <TextOnHoverWhenAdjust
          key="mo uom hovered"
          text={uom?.name || uom || "-"}
          widthOfElement={columnHeaderWidth["uom"] || 100}
        />
      ),
      "PLANNED START": (
        <td key="planned_start" className="centerAlign">
          {plannedStartDate}
        </td>
      ),
      "REQUIRED BY": (
        <td key="required_by" className="centerAlign">
          {requiredByDate}
        </td>
      ),
      "EST COMPLETION": (
        <td key="est_completion" className="centerAlign">
          {estCompletionDate}
        </td>
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="mo whs hovered"
          text={warehouse?.name || "--"}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),
    };
  },
  coManufacturingOrder: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      id,
      name,
      co_manufacturer,
      co_manufacturer_warehouse,
      eta,
      priority_id,
      priority,
      currency,
      created_by,
      destination_warehouse,
      status,
    } = order;

    const [etaDate] = showDateAndTime(eta);

    return {
      "CO-MANF NO": (
        <td key="co-manf no" className="projectName">
          <Link to={`/co-manufacturing-order/${id}`}>
            <TextOnHoverWhenAdjust
              key="co-manf no hovered"
              text={name || "--"}
              widthOfElement={columnHeaderWidth["co-manf no"] || 100}
            />
          </Link>
        </td>
      ),
      "CO-MANF": (
        <TextOnHoverWhenAdjust
          key="co-manf hovered"
          text={co_manufacturer?.display_name || co_manufacturer?.name || "-"}
          widthOfElement={columnHeaderWidth["co-manf"] || 100}
        />
      ),
      "CO-MANF WHS": (
        <TextOnHoverWhenAdjust
          key="co-manf whs hovered"
          text={co_manufacturer_warehouse?.name || "-"}
          widthOfElement={columnHeaderWidth["co-manf whs"] || 100}
        />
      ),
      ETA: (
        <TextOnHoverWhenAdjust
          key="eta hovered"
          text={etaDate || "-"}
          widthOfElement={columnHeaderWidth["eta"] || 100}
        />
      ),
      PRIORITY: (
        <TextOnHoverWhenAdjust
          key="priority hovered"
          text={priority?.name || priority_id || "-"}
          widthOfElement={columnHeaderWidth["priority"] || 100}
        />
      ),
      CURRENCY: (
        <TextOnHoverWhenAdjust
          key="currency hovered"
          text={currency?.code || currency?.name || "-"}
          widthOfElement={columnHeaderWidth["currency"] || 100}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created by hovered"
          text={
            created_by?.first_name ? `${created_by.first_name} ${created_by.last_name || ""}` : "-"
          }
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "DESTINATION WHS": (
        <TextOnHoverWhenAdjust
          key="dest whs hovered"
          text={destination_warehouse?.name || "-"}
          widthOfElement={columnHeaderWidth["destination whs"] || 100}
        />
      ),
      STATUS: (
        <td key="status" className="centerAlign">
          <StatusTag className={status?.name || status}>{status?.name || status}</StatusTag>
        </td>
      ),
    };
  },

  taxRate: ({ order, columnHeaderWidth }) => {
    const { tax_category, name, rate, is_default, date_of_creation, created_by, jurisdiction } =
      order;
    const defaultStatus = is_default ? "Yes" : "No";
    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation);
    return {
      "TAX CATEGORY": (
        <TextOnHoverWhenAdjust
          key="tax category hovered"
          text={tax_category?.name}
          widthOfElement={columnHeaderWidth["tax category"] || 100}
        />
      ),
      NAME: (
        <TextOnHoverWhenAdjust
          key="tax name hovered"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      RATE: (
        <TextOnHoverWhenAdjust
          key="rate hovered"
          text={rate + "%"}
          widthOfElement={columnHeaderWidth["rate"] || 100}
        />
      ),
      JURISDICTION: (
        <TextOnHoverWhenAdjust
          key="jurisdiction hovered"
          text={jurisdiction?.name}
          widthOfElement={columnHeaderWidth["jurisdiction"] || 100}
        />
      ),

      "SYSTEM DEFAULT": (
        <TextOnHoverWhenAdjust
          key="DEFAULT hovered"
          text={defaultStatus}
          widthOfElement={columnHeaderWidth["system default"] || 100}
        />
      ),

      "CREATE DATE": (
        <td key="eta" className="centerAlign" title={createDateWithTime}>
          {createDate ? <> {createDate}</> : "--"}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key=" hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
    };
  },

  taxCategory: ({ order, columnHeaderWidth }) => {
    const { name, description, is_default } = order;
    const defaultStatus = is_default ? "Yes" : "No";

    return {
      "TAX CATEGORY NAME": (
        <TextOnHoverWhenAdjust
          key="tax name hovered"
          text={name}
          widthOfElement={columnHeaderWidth["tax category name"] || 100}
        />
      ),
      "TAX CATEGORY DESCRIPTION": (
        <TextOnHoverWhenAdjust
          key="tax desc hovered"
          text={description || "--"}
          widthOfElement={columnHeaderWidth["tax category description"] || 100}
        />
      ),
      "SYSTEM DEFAULT": (
        <TextOnHoverWhenAdjust
          key="DEFAULT hovered"
          text={defaultStatus}
          widthOfElement={columnHeaderWidth["system default"] || 100}
        />
      ),
    };
  },

  productTaxClassification: ({ order, columnHeaderWidth }) => {
    const { name, is_default, description } = order;
    const defaultStatus = is_default ? "Yes" : "No";
    return {
      "PRODUCT TAX CLASSIFICATION NAME": (
        <TextOnHoverWhenAdjust
          key="product tax classification name"
          text={name}
          widthOfElement={columnHeaderWidth["product tax classification name"] || 100}
        />
      ),
      DESCRIPTION: (
        <TextOnHoverWhenAdjust
          key="product tax classification description"
          text={description || "--"}
          widthOfElement={columnHeaderWidth["description"] || 100}
        />
      ),
      "SYSTEM DEFAULT": (
        <TextOnHoverWhenAdjust
          key="system default"
          text={defaultStatus}
          widthOfElement={columnHeaderWidth["system default"] || 100}
        />
      ),
    };
  },

  chartsOfAccount: ({ order, columnHeaderWidth }) => {
    const {
      name,
      code,
      type,
      currency,
      enable_payments_to_account,
      show_in_expense_claims,
      description,
      created_by,
    } = order;

    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name hovered"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      CODE: (
        <TextOnHoverWhenAdjust
          key="code hovered"
          text={code}
          widthOfElement={columnHeaderWidth["code"] || 100}
        />
      ),
      CURRENCY: (
        <TextOnHoverWhenAdjust
          key="currency hovered"
          text={currency?.name}
          widthOfElement={columnHeaderWidth["currency"] || 100}
        />
      ),
      "ACCOUNT TYPE": (
        <TextOnHoverWhenAdjust
          key="type hovered"
          text={type?.name}
          widthOfElement={columnHeaderWidth["type"] || 100}
        />
      ),
      "ENABLE PAYMENTS TO ACCOUNT": (
        <TextOnHoverWhenAdjust
          key="enable hovered"
          text={enable_payments_to_account ? "Yes" : "No"}
          widthOfElement={columnHeaderWidth["enable payments to account"] || 100}
        />
      ),
      "SHOW IN EXPENSE CLAIM": (
        <TextOnHoverWhenAdjust
          key="expensed  hovered"
          text={show_in_expense_claims ? "Yes" : "No"}
          widthOfElement={columnHeaderWidth["show in expense claims"] || 100}
        />
      ),

      DESCRIPTION: (
        <TextOnHoverWhenAdjust
          key="DES   hovered"
          text={description}
          widthOfElement={columnHeaderWidth["description"] || 100}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key=" hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
    };
  },

  paymentTerm: ({ order, columnHeaderWidth }) => {
    const {
      term_type,
      due_days,
      name,
      months_offset,
      description,
      is_active,
      is_default,
      created_by,
    } = order;

    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name hovered"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 100}
        />
      ),
      "TERM TYPE": (
        <TextOnHoverWhenAdjust
          key="type hovered"
          text={term_type?.name}
          widthOfElement={columnHeaderWidth["term type"] || 100}
        />
      ),
      "DUE DAYS": <td key="dues days offset">{due_days}</td>,
      "MONTH OFFSET": <td key="month offset">{months_offset}</td>,
      DESCRIPTION: (
        <TextOnHoverWhenAdjust
          key="descript hovered"
          text={description}
          widthOfElement={columnHeaderWidth["description"] || 100}
        />
      ),
      ACTIVE: (
        <TextOnHoverWhenAdjust
          key="active hovered"
          text={is_active ? "Yes" : "No"}
          widthOfElement={columnHeaderWidth["active"] || 100}
        />
      ),
      DEFAULT: (
        <TextOnHoverWhenAdjust
          key="DEFAULT hovered"
          text={is_default ? "Yes" : "No"}
          widthOfElement={columnHeaderWidth["default"] || 100}
        />
      ),

      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created hovered"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
    };
  },

  billChargeType: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      amount_type,
      created_by,
      date_of_creation,
      description,
      distribution_type,
      is_landed_cost,
      name,
      unit_price,
    } = order;

    const amountTypeDisplay =
      amount_type?.name ||
      ({ inclusive: "Inclusive", no_tax: "No Tax", exclusive: "Exclusive" }[amount_type] ?? "--");

    const distributionTypeId = distribution_type?.id ?? distribution_type;
    const distributionTypeDisplay =
      ({
        weight: "Weight",
        volume: "Volume",
        volumetric_weight: "Volumetric Weight",
        value: "Standard",
      }[distributionTypeId] ??
        distribution_type?.name) ||
      "--";

    return {
      NAME: (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["name"] || 150}
        />
      ),
      "AMOUNT TYPE": (
        <TextOnHoverWhenAdjust
          key="amount type hovered"
          text={amountTypeDisplay}
          widthOfElement={columnHeaderWidth["amount type"] || 120}
        />
      ),
      "DISTRIBUTION TYPE": (
        <TextOnHoverWhenAdjust
          key="distribution type hovered"
          text={distributionTypeDisplay}
          widthOfElement={columnHeaderWidth["distribution type"] || 140}
        />
      ),
      "UNIT PRICE": (
        <TextOnHoverWhenAdjust
          key="unit price hovered"
          text={formattedAmount(unit_price)}
          widthOfElement={columnHeaderWidth["unit price"] || COLUMN_CONSTRAINTS.MIN_WIDTH}
          textAlign="right"
        />
      ),
      "IS LANDED COST": (
        <TextOnHoverWhenAdjust
          key="landed cost hovered"
          text={is_landed_cost ? "Yes" : "No"}
          widthOfElement={columnHeaderWidth["is landed cost"] || 120}
        />
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={created_by?.full_name || "--"}
          widthOfElement={columnHeaderWidth["created by"] || 140}
        />
      ),
      "DATE OF CREATION": (
        <TextOnHoverWhenAdjust
          key="date_of_creation"
          text={showDateAndTime(date_of_creation)?.[0] || "--"}
          widthOfElement={columnHeaderWidth["date of creation"] || 140}
        />
      ),
      DESCRIPTION: (
        <TextOnHoverWhenAdjust
          key="descript hovered"
          text={description || "-"}
          widthOfElement={columnHeaderWidth["description"] || 200}
        />
      ),
    };
  },

  journalLine: ({ order, columnHeaderWidth, formattedAmount }) => {
    const { amount, account, date_of_creation } = order;

    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation) || [];

    return {
      "ACCOUNT CODE": (
        <TextOnHoverWhenAdjust
          key="account code hovered"
          text={account?.code || "--"}
          widthOfElement={columnHeaderWidth["account code"] || 100}
        />
      ),
      "ACCOUNT NAME": (
        <TextOnHoverWhenAdjust
          key="account name hovered"
          text={account?.name || "--"}
          widthOfElement={columnHeaderWidth["account name"] || 100}
        />
      ),
      AMOUNT: (
        <TextOnHoverWhenAdjust
          key="amount hovered"
          text={
            amount != null ? (formattedAmount ? formattedAmount(amount) : String(amount)) : "--"
          }
          widthOfElement={columnHeaderWidth["amount"] || 100}
          textAlign="right"
        />
      ),
      "CREATE DATE": (
        <td key="date_of_creation" className="centerAlign" title={createDateWithTime}>
          {createDate || ""}
        </td>
      ),
    };
  },

  syncHistory: ({ order, columnHeaderWidth }) => {
    const { status, comment, action, external_system, start_time } = order;

    const [date, dateWithTime] = showDateAndTime(start_time || null) || [];
    const time = dayjs(start_time).format("hh:mm A");

    const Icon = external_system?.code ? integrationConfig[external_system?.code]?.icon : "--";

    return {
      APPLICATION: <td style={{ paddingLeft: "40px" }}>{Icon}</td>,

      STATUS: (
        <TextOnHoverWhenAdjust
          key="status hovered"
          text={status?.name || "--"}
          widthOfElement={columnHeaderWidth["status"] || 100}
        />
      ),

      DATE: (
        <td key="sync_date" className="centerAlign" title={dateWithTime}>
          {date ? date : ""}
        </td>
      ),

      TIME: (
        <td key="sync_time" className="centerAlign" title={dateWithTime}>
          {time}
        </td>
      ),

      "SYNC ACTION": (
        <TextOnHoverWhenAdjust
          key="module hovered"
          text={action || "--"}
          widthOfElement={columnHeaderWidth["sync action"] || 100}
        />
      ),

      COMMENT: (
        <TextOnHoverWhenAdjust
          key="action hovered"
          text={comment || "--"}
          widthOfElement={columnHeaderWidth["comment"] || 100}
        />
      ),
    };
  },

  shopifyStores: ({ order, columnHeaderWidth }) => {
    const { id, store_name, shop, is_authenticated, setup_issues: setupIssues = [] } = order;
    const storeStatus = shopifyStoreStatus(is_authenticated, setupIssues);

    return {
      "STORE NAME": (
        <TextOnHoverWhenAdjust
          key="store name hovered"
          text={<span title={shop || undefined}>{store_name || shop || notAvailable}</span>}
          widthOfElement={columnHeaderWidth["store name"] || 100}
        />
      ),

      STATUS: (
        <td key="store_status" title={storeStatus.hint}>
          <StatusTag className={storeStatus.tone}>{storeStatus.label}</StatusTag>
        </td>
      ),

      ACTIVE: <ShopifyStoreActiveToggle key="store_active" store={order} />,

      "PRODUCT CATALOGUE": (
        <TextOnHoverWhenAdjust
          key="store catalogue hovered"
          text={
            <Link
              to={`/integration/shopify/${id}/catalogue`}
              style={{ color: "#1249bf", fontWeight: 500 }}
            >
              View Products
            </Link>
          }
          widthOfElement={columnHeaderWidth["product catalogue"] || 100}
        />
      ),

      MANAGE: (
        <TextOnHoverWhenAdjust
          key="store manage hovered"
          text={
            <Link
              to={`/integration/shopify/${id}/settings`}
              style={{ color: "#1249bf", fontWeight: 500 }}
            >
              Manage Settings
            </Link>
          }
          widthOfElement={columnHeaderWidth["manage"] || 100}
        />
      ),
    };
  },

  shopifyCatalogue: ({ order, columnHeaderWidth }) => {
    const { sku, listed, available_stock, pricelist_name, allow_backorder, locations } = order;

    const stockLabel =
      available_stock === null || available_stock === undefined || available_stock === ""
        ? "—"
        : Number(available_stock).toLocaleString(undefined, { maximumFractionDigits: 2 });

    return {
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="catalogue product hovered"
          text={sku || "—"}
          widthOfElement={columnHeaderWidth["product"] || 160}
        />
      ),
      STATUS: (
        <td key="status">
          <StatusTag className={listed ? "Active" : "In Active"}>
            {listed ? "Listed" : "Not Listed"}
          </StatusTag>
        </td>
      ),
      "AVAILABLE STOCK": (
        <TextOnHoverWhenAdjust
          key="catalogue stock hovered"
          text={stockLabel}
          widthOfElement={columnHeaderWidth["available stock"] || 100}
        />
      ),
      PRICELIST: (
        <TextOnHoverWhenAdjust
          key="catalogue pricelist hovered"
          text={pricelist_name || "—"}
          widthOfElement={columnHeaderWidth["pricelist"] || 120}
        />
      ),
      "ALLOW BACKORDER": (
        <TextOnHoverWhenAdjust
          key="allow_backorder"
          text={allow_backorder ? "On" : "Off"}
          widthOfElement={columnHeaderWidth["allow backorder"] || 120}
        />
      ),
      FACILITIES: (
        <td key="locations">
          <EllipsisMultiText
            text={locations?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["facilities"] || 100}
          >
            {locations?.length
              ? locations?.map((item, i) => (
                  <Tag key={`locations-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
    };
  },

  shopifyLinkedProducts: ({ order, columnHeaderWidth }) => {
    const {
      name,
      sku,
      shopify_title,
      shopify_available,
      shopify_on_hand,
      on_order_qty,
      next_eta,
      purchase_orders: purchaseOrders = [],
      stock_by_location: stockByLocation = [],
    } = order;

    const formatQty = value =>
      value === null || value === undefined || value === ""
        ? "--"
        : Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
    const formatEta = value => (value ? format(parseISO(value), "dd MMM yyyy") : "--");
    const purchaseOrderLabel = po =>
      `${po.po}: ${formatQty(po.qty)}${po.eta ? ` due ${formatEta(po.eta)}` : ""}`;
    const managedByOrderlink = entry => entry.stock_source === "orderlink";
    const locationLabel = entry =>
      `${entry.warehouse_name}: ${formatQty(entry.available)}${
        managedByOrderlink(entry) ? " · Orderlink" : ""
      }`;
    const locationDetail = entry =>
      `${entry.warehouse_name}: ${formatQty(entry.available)} available, ${formatQty(
        entry.on_hand,
      )} on hand${managedByOrderlink(entry) ? " (Orderlink adds receipts and adjustments)" : ""}`;

    return {
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="linked product hovered"
          text={
            <span title={sku ? `${name} (${sku})` : name}>
              {name || notAvailable}
              {sku ? <span style={{ color: "#5c6679" }}> · {sku}</span> : null}
            </span>
          }
          widthOfElement={columnHeaderWidth["product"] || 200}
        />
      ),
      "SHOPIFY PRODUCT": (
        <TextOnHoverWhenAdjust
          key="linked shopify title hovered"
          text={shopify_title || "--"}
          widthOfElement={columnHeaderWidth["shopify product"] || 160}
        />
      ),
      "SHOPIFY AVAILABLE": (
        <TextOnHoverWhenAdjust
          key="linked shopify available hovered"
          text={formatQty(shopify_available)}
          widthOfElement={columnHeaderWidth["shopify available"] || 100}
        />
      ),
      "SHOPIFY ON HAND": (
        <TextOnHoverWhenAdjust
          key="linked shopify on hand hovered"
          text={formatQty(shopify_on_hand)}
          widthOfElement={columnHeaderWidth["shopify on hand"] || 100}
        />
      ),
      "STOCK BY LOCATION": (
        <td key="linked stock by location">
          <EllipsisMultiText
            text={stockByLocation.map(locationDetail).join(", ")}
            width={columnHeaderWidth["stock by location"] || 200}
          >
            {stockByLocation.length
              ? stockByLocation.map(entry => (
                  <Tag
                    key={`linked-location-${entry.location_id}`}
                    className="in-table"
                    title={locationDetail(entry)}
                  >
                    {locationLabel(entry)}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      "ON ORDER": (
        <TextOnHoverWhenAdjust
          key="linked on order hovered"
          text={Number(on_order_qty) > 0 ? formatQty(on_order_qty) : "--"}
          widthOfElement={columnHeaderWidth["on order"] || 100}
        />
      ),
      "NEXT ETA": (
        <TextOnHoverWhenAdjust
          key="linked next eta hovered"
          text={formatEta(next_eta)}
          widthOfElement={columnHeaderWidth["next eta"] || 110}
        />
      ),
      "OPEN PURCHASE ORDERS": (
        <td key="linked purchase orders">
          <EllipsisMultiText
            text={purchaseOrders.map(purchaseOrderLabel).join(", ")}
            width={columnHeaderWidth["open purchase orders"] || 200}
          >
            {purchaseOrders.length
              ? purchaseOrders.map(po => (
                  <Tag key={`linked-po-${po.po}`} className="in-table">
                    {purchaseOrderLabel(po)}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
    };
  },
  prePayment: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      name,
      supplier,
      reference,
      payment_type,
      account,
      bank_account_dummy,
      currency,
      date_paid,
      amount,
      created_by,
      date_of_creation,
      status,
    } = order || {};

    const [createDate, createDateWithTime] = showDateAndTime(date_of_creation) || [];
    const [datePaid, datePaidWithTime] = showDateAndTime(date_paid) || [];

    const supplierName = supplier?.name ?? <span className="dataNotPresent">Not Available</span>;
    const currencyField = currency?.name ?? "";

    const paymentNoCell = (
      <td key="prepayment_no">
        <PrepaymentPaymentNoCell prepayment={order} name={name} notAvailable={notAvailable} />
      </td>
    );

    return {
      "PAYMENT NO": paymentNoCell,
      "PREPAYMENT NO": paymentNoCell,
      SUPPLIER: (
        <TextOnHoverWhenAdjust
          key={"supplier"}
          text={supplierName}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ),
      REFERENCE: (
        <TextOnHoverWhenAdjust
          key={"reference"}
          text={reference}
          widthOfElement={columnHeaderWidth["reference"] || 100}
        />
      ),
      "PAYMENT TYPE": <td key="payment_type">{payment_type?.name ?? notAvailable}</td>,
      "PURCHASE ORDERS": (
        <td key="purchase_orders">
          <PrepaymentPurchaseOrdersCell
            order={order}
            width={columnHeaderWidth["purchase orders"] || 100}
          />
        </td>
      ),
      ACCOUNT: (
        <TextOnHoverWhenAdjust
          key={"account"}
          text={account?.name}
          widthOfElement={columnHeaderWidth["account"] || 100}
        />
      ),
      "BANK ACCOUNT": (
        <TextOnHoverWhenAdjust
          key={"bank_account"}
          text={bank_account_dummy?.name}
          widthOfElement={columnHeaderWidth["bank account"] || 100}
        />
      ),
      CURRENCY: (
        <TextOnHoverWhenAdjust
          key="currency"
          text={currencyField}
          widthOfElement={columnHeaderWidth["currency"] || 100}
        />
      ),
      "DATE PAID": (
        <td key="date_paid" title={datePaidWithTime} className="centerAlign">
          {datePaid ?? ""}
        </td>
      ),
      AMOUNT: (
        <td key="amount" className="rightAlign">
          {formattedAmount(amount)}
        </td>
      ),
      "CREATED BY": (
        <td key={"created by"} className="userPic">
          {created_by ? created_by?.full_name : notAvailable}
        </td>
      ),
      "CREATE DATE": (
        <td key="create_date" title={createDateWithTime} className="centerAlign">
          {createDate ?? ""}
        </td>
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={status?.name ?? status} />
        </td>
      ),
    };
  },
  saleInvoice: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      create_date,
      currency,
      customer_name,
      customer,
      due_date,
      name,
      reference_no,
      status,
      sub_total,
      tax_amount,
      total_amount,
      amount_due,
      amount_paid,
      managed_in_xero,
      xero_amount_due,
    } = order || {};
    const amountDue = (() => {
      if (managed_in_xero && xero_amount_due != null && xero_amount_due !== "") {
        return xero_amount_due;
      }
      if (amount_due != null && amount_due !== "") {
        return amount_due;
      }
      if (["sent", "overdue", "approved"].includes(status?.id)) {
        const total = Number(total_amount) || 0;
        const paid = Number(amount_paid) || 0;
        return Math.max(total - paid, 0);
      }
      return null;
    })();

    const [createDate, createDateWithTime] = showDateAndTime(create_date) || [];
    const [dueDate, dueDateWithTime] = showDateAndTime(due_date) || [];
    const customerName = customer_name || customer?.display_name || (
      <span className="dataNotPresent">Not Available</span>
    );
    const fulfillmentStatusCell = key =>
      saleInvoiceFulfillmentListCell({
        cellKey: key,
        columnHeaderWidth,
        order,
      });

    return {
      "INVOICE NO": (
        <td key="invoice_no" className="projectName">
          {name ? <Link to={`/sale_invoice/${order.id}`}>{name}</Link> : notAvailable}
        </td>
      ),
      CUSTOMER: (
        <TextOnHoverWhenAdjust
          key="customer"
          text={customerName}
          widthOfElement={columnHeaderWidth["customer"] || 150}
        />
      ),
      "CREATE DATE": (
        <td key="create_date" title={createDateWithTime} className="centerAlign">
          {createDate ?? ""}
        </td>
      ),
      "DUE DATE": (
        <td key="due_date" title={dueDateWithTime} className="centerAlign">
          {dueDate ?? ""}
        </td>
      ),
      "REFERENCE NO": (
        <TextOnHoverWhenAdjust
          key="reference_no"
          text={reference_no}
          widthOfElement={columnHeaderWidth["reference no"] || 120}
        />
      ),
      "SUB TOTAL": (
        <td key="sub_total" className="rightAlign">
          {formattedAmount(sub_total)}
        </td>
      ),
      TAX: (
        <td key="tax_amount" className="rightAlign">
          {formattedAmount(tax_amount)}
        </td>
      ),
      TOTAL: (
        <td key="total_amount" className="rightAlign">
          {formattedAmount(total_amount)}
        </td>
      ),
      "AMOUNT DUE": (
        <td key="amount_due" className="rightAlign">
          {amountDue != null ? formattedAmount(amountDue) : notAvailable}
        </td>
      ),
      CURRENCY: (
        <TextOnHoverWhenAdjust
          key="currency"
          text={currency?.code}
          widthOfElement={columnHeaderWidth["currency"] || 80}
        />
      ),
      ISSUES: fulfillmentStatusCell("issues"),
      FULFILLMENT: fulfillmentStatusCell("fulfillment"),
      "FULFILLMENT STATUS": fulfillmentStatusCell("fulfillment_status"),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={status?.name ?? status} />
        </td>
      ),
    };
  },
  stockReplenishment: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      product,
      name,
      confirmed_qty,
      suggested_qty,
      source_locations,
      destination_location,
      status,
      warehouse,
      created_by,
      date_of_creation,
    } = order || {};

    const [creationDate, creationDateWithTime] = showDateAndTime(date_of_creation);

    return {
      "REPLENISH NO": (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["replenish no"] || 100}
        />
      ),
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product"
          text={product?.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      "SUGGESTED QUANTITY": (
        <td key="suggested_qty" className="rightAlign">
          {formattedAmount(suggested_qty)}
        </td>
      ),
      "CONFIRM QTY": (
        <td key="confirmed_qty" className="rightAlign">
          {formattedAmount(confirmed_qty)}
        </td>
      ),
      "SOURCE LOCATIONS": (
        <td key="source_locations">
          <EllipsisMultiText
            text={source_locations?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["source locations"] || 100}
          >
            {source_locations?.length
              ? source_locations.map((item, i) => (
                  <Tag key={`src-loc-${name}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      "DESTINATION LOCATION": (
        <TextOnHoverWhenAdjust
          key="destination_location"
          text={destination_location?.name}
          widthOfElement={columnHeaderWidth["destination location"] || 100}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="warehouse"
          text={warehouse?.name}
          widthOfElement={columnHeaderWidth["facility"] || 100}
        />
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={status?.name ?? status} />
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={created_by?.full_name}
          widthOfElement={columnHeaderWidth["created by"] || 100}
        />
      ),
      "CREATE DATE": (
        <td key="date_of_creation" className="centerAlign" title={creationDateWithTime}>
          {creationDate ? creationDate : ""}
        </td>
      ),
    };
  },

  plannedOrder: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      currency,
      order_qty,
      planned_delivery_date,
      planned_order_date,
      product_plan,
      status,
      total_cost,
    } = order || {};
    const { product, supplier, from_warehouse, to_warehouse, acquisition_code } =
      product_plan || {};

    const [orderDate, orderDateWithTime] = showDateAndTime(planned_order_date);
    const [requiredDate, requiredDateWithTime] = showDateAndTime(planned_delivery_date);

    return {
      PRODUCT: (
        <TextOnHoverWhenAdjust
          key="product"
          text={product?.number}
          widthOfElement={columnHeaderWidth["product"] || 100}
        />
      ),
      SUPPLIER: (
        <TextOnHoverWhenAdjust
          key="supplier"
          text={supplier?.name}
          widthOfElement={columnHeaderWidth["supplier"] || 100}
        />
      ),
      "FROM FACILITY": (
        <TextOnHoverWhenAdjust
          key="from_warehouse"
          text={from_warehouse?.name}
          widthOfElement={columnHeaderWidth["from facility"] || 100}
        />
      ),
      "TO FACILITY": (
        <TextOnHoverWhenAdjust
          key="to_warehouse"
          text={to_warehouse?.name}
          widthOfElement={columnHeaderWidth["to facility"] || 100}
        />
      ),
      "ORDER DATE": (
        <td key="planned_order_date" className="centerAlign" title={orderDateWithTime}>
          {orderDate ? orderDate : ""}
        </td>
      ),
      "REQUIRED DATE": (
        <td key="planned_delivery_date" className="centerAlign" title={requiredDateWithTime}>
          {requiredDate ? requiredDate : ""}
        </td>
      ),
      "ORDER QTY": (
        <td key="order_qty" className="rightAlign">
          {formattedAmount(order_qty)}
        </td>
      ),
      "ORDER VALUE": (
        <td key="total_cost" className="rightAlign">
          {currency ? `${currency} ` : ""}
          {formattedAmount(total_cost)}
        </td>
      ),
      "ORDER TYPE": (
        <TextOnHoverWhenAdjust
          key="acquisition_code"
          text={acquisition_code}
          widthOfElement={columnHeaderWidth["order type"] || 100}
        />
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={status?.name ?? status} />
        </td>
      ),
    };
  },

  customerSegment: ({ order, columnHeaderWidth }) => {
    const {
      name,
      countries,
      states,
      cities,
      post_codes,
      status,
      types,
      sub_types,
      customer_statuses,
      payment_terms,
      tags,
    } = order || {};

    return {
      "CUSTOMER SEGMENT NAME": (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["customer segment name"] || 150}
        />
      ),
      COUNTRY: (
        <td key="countries">
          <EllipsisMultiText
            text={countries?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["country"] || 150}
          >
            {countries?.length
              ? countries?.map((item, i) => (
                  <Tag key={`countries-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      "REGION / STATE": (
        <td key="states">
          <EllipsisMultiText
            text={states?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["region / state"] || 150}
          >
            {states?.length
              ? states?.map((item, i) => (
                  <Tag key={`states-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      CITY: (
        <td key="cities">
          <EllipsisMultiText
            text={cities?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["city"] || 150}
          >
            {cities?.length
              ? cities?.map((item, i) => (
                  <Tag key={`cities-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      POSTCODE: (
        <td key="post_codes">
          <EllipsisMultiText
            text={post_codes?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["postcode"] || 150}
          >
            {post_codes?.length
              ? post_codes?.map((item, i) => (
                  <Tag key={`post_codes-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={status?.name || status} />
        </td>
      ),
      "CUSTOMER TYPE": (
        <td key="types">
          <EllipsisMultiText
            text={types?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["customer type"] || 100}
          >
            {types?.length
              ? types?.map((item, i) => (
                  <Tag key={`types-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      "CUSTOMER SUB TYPE": (
        <td key="sub_types">
          <EllipsisMultiText
            text={sub_types?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["customer sub type"] || 100}
          >
            {sub_types?.length
              ? sub_types?.map((item, i) => (
                  <Tag key={`sub_types-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      "ACCOUNT STATUS": (
        <td key="customer_statuses">
          <EllipsisMultiText
            text={customer_statuses?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["account status"] || 100}
          >
            {customer_statuses?.length
              ? customer_statuses?.map((item, i) => (
                  <Tag key={`customer_statuses-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      "PAYMENT TERMS": (
        <td key="payment_terms">
          <EllipsisMultiText
            text={payment_terms?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["payment terms"] || 100}
          >
            {payment_terms?.length
              ? payment_terms?.map((item, i) => (
                  <Tag key={`payment_terms-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      TAGS: (
        <td key="tags">
          <EllipsisMultiText
            text={tags?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["tags"] || 100}
          >
            {tags?.length
              ? tags?.map((item, i) => (
                  <Tag key={`tags-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
    };
  },

  salesRep: ({ order, columnHeaderWidth }) => {
    const { user, full_name, created_by, date_of_creation, is_active, is_pending } = order || {};
    const [creationDate, creationDateWithTime] = showDateAndTime(date_of_creation);

    const statusOfRep = is_pending ? "Pending" : is_active ? "Active" : "Inactive";

    return {
      "FULL NAME": (
        <TextOnHoverWhenAdjust
          key="full name"
          text={user?.full_name || full_name}
          widthOfElement={columnHeaderWidth["full name"] || 150}
        />
      ),
      "CREATED BY": (
        <td key="created_by" className="userPic">
          {created_by ? created_by.full_name : notAvailable}
        </td>
      ),
      "CREATE DATE": (
        <td key="create_date" className="centerAlign" title={creationDateWithTime}>
          {creationDate || ""}
        </td>
      ),
      "REP STATUS": (
        <TextOnHoverWhenAdjust
          key="rep status"
          text={statusOfRep}
          widthOfElement={columnHeaderWidth["rep status"] || 100}
        />
      ),
    };
  },

  pricelist: ({ order, columnHeaderWidth }) => {
    const { name, status, countries, channels, validity_date, supply_warehouses } = order || {};

    const [validUntil] = showDateAndTime(validity_date) || [];

    return {
      "PRICELIST NAME": (
        <TextOnHoverWhenAdjust
          key="name"
          text={name}
          widthOfElement={columnHeaderWidth["pricelist name"] || 150}
        />
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={status?.name || status} />
        </td>
      ),
      "SUPPLYING FACILITY": (
        <td key="supply_warehouses">
          <EllipsisMultiText
            text={supply_warehouses?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["supplying facility"] || 125}
          >
            {supply_warehouses?.length
              ? supply_warehouses?.map((item, i) => (
                  <Tag key={`supply_warehouses-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      COUNTRY: (
        <td key="countries">
          <EllipsisMultiText
            text={countries?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["country"] || 150}
          >
            {countries?.length
              ? countries?.map((item, i) => (
                  <Tag key={`countries-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      CHANNEL: (
        <td key="channels">
          <EllipsisMultiText
            text={channels?.map(item => item.name).join(", ")}
            width={columnHeaderWidth["channel"] || 150}
          >
            {channels?.length
              ? channels?.map((item, i) => (
                  <Tag key={`channels-${item.id}-${i}`} className="in-table">
                    {item.name}
                  </Tag>
                ))
              : "--"}
          </EllipsisMultiText>
        </td>
      ),
      "VALIDITY DATE": (
        <td key="validity_date" className="centerAlign">
          {validUntil || "--"}
        </td>
      ),
    };
  },

  promo: ({ order, columnHeaderWidth }) => {
    const [startDate, startDateWithTime] = showDateAndTime(order.start_date);
    const [endDate, endDateWithTime] = showDateAndTime(order.end_date);
    return {
      TITLE: (
        <TextOnHoverWhenAdjust
          key="title"
          text={order.title || order.code || "--"}
          widthOfElement={columnHeaderWidth["title"] || 150}
        />
      ),
      METHOD: <td key="method">{order.method?.name || "--"}</td>,
      "DISCOUNT TYPE": (
        <TextOnHoverWhenAdjust
          key="discount_type"
          text={order.discount_type?.name || "--"}
          widthOfElement={columnHeaderWidth["discount type"] || 150}
        />
      ),
      "VALUE TYPE": (
        <td key="value_type" className="centerAlign">
          {order.value_type?.name || "--"}
        </td>
      ),
      "DISCOUNT VALUE": (
        <td key="discount_value" className="rightAlign">
          {order.discount_value || "--"}
        </td>
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={order.status?.name} />
        </td>
      ),
      "START DATE": (
        <td key="start_date" className="centerAlign" title={startDateWithTime}>
          {startDate || "--"}
        </td>
      ),
      "END DATE": (
        <td key="end_date" className="centerAlign" title={endDateWithTime}>
          {endDate || "--"}
        </td>
      ),
    };
  },
  quote: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      assigned_to,
      customer,
      customer_name,
      salesperson,
      validity_date,
      total_amount,
      tax_amount,
      sub_total,
      status,
      pricelist,
      requested_ship_date,
      currency,
      customer_reference,
      bill_to,
      ship_to,
      billing_address,
      shipping_address,
    } = order || {};

    const [validUntil] = showDateAndTime(validity_date) || [];
    const [reqShipDate] = showDateAndTime(requested_ship_date) || [];

    return {
      CUSTOMER: (
        <TextOnHoverWhenAdjust
          key="customer"
          text={displayText(customer_name, customer?.display_name, customer)}
          widthOfElement={columnHeaderWidth["customer"] || 175}
        />
      ),
      "EXPIRY DATE": (
        <td key="expiry_date" className="centerAlign">
          {validUntil || "--"}
        </td>
      ),
      "PRICE LIST": (
        <TextOnHoverWhenAdjust
          key="price list"
          text={displayText(pricelist?.name, pricelist)}
          widthOfElement={columnHeaderWidth["price list"] || 150}
        />
      ),
      "SALES REP": (
        <TextOnHoverWhenAdjust
          key="sales rep"
          text={salesRepDisplay(assigned_to !== undefined ? assigned_to : salesperson)}
          widthOfElement={columnHeaderWidth["sales rep"] || 150}
        />
      ),
      "REQUESTED SHIP DATE": (
        <td key="requested_ship_date" className="centerAlign">
          {reqShipDate || "--"}
        </td>
      ),
      CURRENCY: (
        <TextOnHoverWhenAdjust
          key="currency"
          text={displayText(currency?.name, currency?.code, currency)}
          widthOfElement={columnHeaderWidth["currency"] || 100}
        />
      ),
      "CUSTOMER REFERENCE": (
        <TextOnHoverWhenAdjust
          key="customer reference"
          text={customer_reference}
          widthOfElement={columnHeaderWidth["customer reference"] || 150}
        />
      ),
      "BILLING ADDRESS": (
        <TextOnHoverWhenAdjust
          key="billing address"
          text={formatDocumentAddress(billing_address, bill_to)}
          widthOfElement={columnHeaderWidth["billing address"] || 200}
        />
      ),
      "SHIPPING ADDRESS": (
        <TextOnHoverWhenAdjust
          key="shipping address"
          text={formatDocumentAddress(shipping_address, ship_to)}
          widthOfElement={columnHeaderWidth["shipping address"] || 200}
        />
      ),
      "SUB TOTAL": (
        <TextOnHoverWhenAdjust
          key="sub_total"
          text={formattedAmount(sub_total)}
          widthOfElement={columnHeaderWidth["sub total"] || 125}
          textAlign="right"
        />
      ),
      TAX: (
        <TextOnHoverWhenAdjust
          key="tax"
          text={formattedAmount(tax_amount)}
          widthOfElement={columnHeaderWidth["tax"] || 125}
          textAlign="right"
        />
      ),
      "TOTAL AMOUNT": (
        <TextOnHoverWhenAdjust
          key="total_amount"
          text={formattedAmount(total_amount)}
          widthOfElement={columnHeaderWidth["total amount"] || 125}
          textAlign="right"
        />
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={displayText(status?.name, status)} />
        </td>
      ),
    };
  },

  salesOrder: ({ order, columnHeaderWidth, formattedAmount }) => {
    const {
      assigned_to,
      bill_to,
      billing_address,
      currency,
      customer_name,
      customer_reference,
      customer,
      from_warehouse,
      order_date,
      pricelist,
      requested_ship_date,
      ship_to,
      shipping_address,
      status,
      sub_total,
      tax_amount,
      total_amount,
    } = order || {};

    const [orderDate] = showDateAndTime(order_date) || [];
    const [reqShipDate] = showDateAndTime(requested_ship_date) || [];

    return {
      "ORDER DATE": (
        <td key="order_date" className="centerAlign">
          {orderDate || "--"}
        </td>
      ),
      CUSTOMER: (
        <TextOnHoverWhenAdjust
          key="customer"
          text={customer_name || customer?.display_name || customer}
          widthOfElement={columnHeaderWidth["customer"] || 175}
        />
      ),
      FACILITY: (
        <TextOnHoverWhenAdjust
          key="warehouse"
          text={from_warehouse?.name || from_warehouse}
          widthOfElement={columnHeaderWidth["facility"] || 150}
        />
      ),
      PRICELIST: (
        <TextOnHoverWhenAdjust
          key="pricelist"
          text={pricelist?.name || pricelist}
          widthOfElement={columnHeaderWidth["pricelist"] || 150}
        />
      ),
      SALESPERSON: (
        <TextOnHoverWhenAdjust
          key="salesperson"
          text={salesRepDisplay(assigned_to)}
          widthOfElement={columnHeaderWidth["salesperson"] || 150}
        />
      ),
      "REQUESTED SHIP DATE": (
        <td key="requested_ship_date" className="centerAlign">
          {reqShipDate || "--"}
        </td>
      ),
      CURRENCY: (
        <TextOnHoverWhenAdjust
          key="currency"
          text={currency?.code || currency?.name || currency}
          widthOfElement={columnHeaderWidth["currency"] || 100}
        />
      ),
      "CUSTOMER REFERENCE": (
        <TextOnHoverWhenAdjust
          key="customer reference"
          text={customer_reference}
          widthOfElement={columnHeaderWidth["customer reference"] || 150}
        />
      ),
      "BILLING ADDRESS": (
        <TextOnHoverWhenAdjust
          key="billing address"
          text={formatDocumentAddress(billing_address, bill_to)}
          widthOfElement={columnHeaderWidth["billing address"] || 200}
        />
      ),
      "SHIPPING ADDRESS": (
        <TextOnHoverWhenAdjust
          key="shipping address"
          text={formatDocumentAddress(shipping_address, ship_to)}
          widthOfElement={columnHeaderWidth["shipping address"] || 200}
        />
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={status?.name || status} />
        </td>
      ),
      "SUB TOTAL": (
        <TextOnHoverWhenAdjust
          key="subtotal"
          text={formattedAmount ? formattedAmount(sub_total) : sub_total}
          widthOfElement={columnHeaderWidth["sub total"] || 125}
          textAlign="right"
        />
      ),
      TAX: (
        <TextOnHoverWhenAdjust
          key="tax"
          text={formattedAmount ? formattedAmount(tax_amount) : tax_amount}
          widthOfElement={columnHeaderWidth["tax"] || 125}
          textAlign="right"
        />
      ),
      TOTAL: (
        <TextOnHoverWhenAdjust
          key="total"
          text={formattedAmount ? formattedAmount(total_amount) : total_amount}
          widthOfElement={columnHeaderWidth["total"] || 125}
          textAlign="right"
        />
      ),
    };
  },
  delivery: ({ order, columnHeaderWidth, formattedAmount }) => {
    const [dispatchDate, dispatchDateWithTime] = showDateAndTime(order.planned_dispatch_date) || [];

    return {
      "DELIVERY TYPE": (
        <TextOnHoverWhenAdjust
          key="delivery_type"
          text={order.movement_type?.name}
          widthOfElement={columnHeaderWidth["delivery type"] || 100}
        />
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={order.status?.name} />
        </td>
      ),
      ORIGIN: (
        <TextOnHoverWhenAdjust
          key="origin"
          text={order.from_warehouse?.name}
          widthOfElement={columnHeaderWidth["origin"] || 120}
        />
      ),
      DESTINATION: (
        <TextOnHoverWhenAdjust
          key="destination"
          text={formatAddress(order.ship_to)}
          widthOfElement={columnHeaderWidth["destination"] || 150}
        />
      ),
      "DISPATCH DATE": (
        <td key="dispatch_date" className="centerAlign" title={dispatchDateWithTime}>
          {dispatchDate || "--"}
        </td>
      ),
      "SERVICE LEVEL": (
        <TextOnHoverWhenAdjust
          key="service_level"
          text={order.service_level?.name}
          widthOfElement={columnHeaderWidth["service level"] || 100}
        />
      ),
      PRIORITY: (
        <TextOnHoverWhenAdjust
          key="priority"
          text={order.priority?.name || order.priority}
          widthOfElement={columnHeaderWidth["priority"] || 80}
        />
      ),
      CUSTOMER: (
        <TextOnHoverWhenAdjust
          key="customer"
          text={order.customer?.display_name}
          widthOfElement={columnHeaderWidth["customer"] || 150}
        />
      ),
      "DANGEROUS GOODS": (
        <TextOnHoverWhenAdjust
          key="dangerous_goods"
          text={order.is_dangerous_goods ? "Yes" : "No"}
          widthOfElement={columnHeaderWidth["dangerous goods"] || 120}
        />
      ),
      "CUSTOMS VALUE": (
        <TextOnHoverWhenAdjust
          key="customs_value"
          text={formattedAmount(order.customer_value)}
          widthOfElement={columnHeaderWidth["customs value"] || 120}
          textAlign="right"
        />
      ),
      "INSURANCE VALUE": (
        <TextOnHoverWhenAdjust
          key="insurance_value"
          text={formattedAmount(order.insurance_value)}
          widthOfElement={columnHeaderWidth["insurance value"] || 120}
          textAlign="right"
        />
      ),
    };
  },
  shipmentV1: ({ order, columnHeaderWidth }) => {
    const [creationDate, creationDateWithTime] = showDateAndTime(order?.date_of_creation) || [];
    return {
      "SHIPMENT NO": (
        <TextOnHoverWhenAdjust
          text={
            <Link to={`/shipment/${order.id}`} style={{ color: "#1249bf", fontWeight: 500 }}>
              {order.name}
            </Link>
          }
          widthOfElement={columnHeaderWidth["shipment no"] || 100}
        />
      ),
      "SHIPMENT TYPE": (
        <TextOnHoverWhenAdjust
          key="shipment_type"
          text={order?.document_type?.name}
          widthOfElement={columnHeaderWidth["shipment type"] || 100}
        />
      ),
      "CREATE DATE": (
        <td key="date_of_creation" className="centerAlign" title={creationDateWithTime}>
          {creationDate ? creationDate : ""}
        </td>
      ),
      STATUS: (
        <td key="status" className="status">
          <Status statusName={order?.status?.name} />
        </td>
      ),
      "FREIGHT FORWARDER": (
        <TextOnHoverWhenAdjust
          key="freight_forwarder"
          text={order?.freight_forwarder}
          widthOfElement={columnHeaderWidth["freight forwarder"] || 100}
        />
      ),
      CUSTOMER: (
        <TextOnHoverWhenAdjust
          key="customer"
          text={order?.customer?.name}
          widthOfElement={columnHeaderWidth["customer"] || 100}
        />
      ),
      "FREIGHT TYPE": (
        <TextOnHoverWhenAdjust
          key="freight_type"
          text={order?.freight_type?.name}
          widthOfElement={columnHeaderWidth["freight type"] || 100}
        />
      ),
      "TOTAL FREIGHT COST": (
        <TextOnHoverWhenAdjust
          key="total_freight_cost"
          text={order?.total_freight_cost}
          widthOfElement={columnHeaderWidth["total freight cost"] || 100}
          textAlign="right"
        />
      ),
      "FROM FACILITY": (
        <TextOnHoverWhenAdjust
          key="from_warehouse"
          text={order?.from_warehouse?.name}
          widthOfElement={columnHeaderWidth["from facility"] || 100}
        />
      ),
      "MOVEMENT TYPE": (
        <TextOnHoverWhenAdjust
          key="movement_type"
          text={order?.movement_type?.name}
          widthOfElement={columnHeaderWidth["movement type"] || 100}
        />
      ),
      PRIORITY: (
        <TextOnHoverWhenAdjust
          key="priority"
          text={order?.priority?.name}
          widthOfElement={columnHeaderWidth["priority"] || 100}
        />
      ),
      INCOTERM: (
        <TextOnHoverWhenAdjust
          key="incoterm"
          text={order?.incoterm?.name}
          widthOfElement={columnHeaderWidth["incoterm"] || 100}
        />
      ),
    };
  },
  asn: ({ order, columnHeaderWidth }) => {
    const [creationDate, creationDateWithTime] = showDateAndTime(order?.date_of_creation) || [];
    const [etaDate, etaDateWithTime] = showDateAndTime(order?.consignment?.eta) || [];
    const suppliers = Array.isArray(order?.consignment?.suppliers)
      ? order.consignment.suppliers
          .map(s => s.name)
          .filter(Boolean)
          .join(", ")
      : "";
    const fromWarehouses = Array.isArray(order?.consignment?.from_warehouses)
      ? order.consignment.from_warehouses
          .map(w => w.name)
          .filter(Boolean)
          .join(", ")
      : "";
    const toWarehouse =
      order?.consignment?.to_warehouse?.name || order?.consignment?.to_warehouse || "";
    const statusName = typeof order?.status === "object" ? order?.status?.name : order?.status;

    return {
      "ASN NO": (
        <TextOnHoverWhenAdjust
          text={
            <Link to={`/shipment/asn/${order.id}`} style={{ color: "#1249bf", fontWeight: 500 }}>
              {order.name}
            </Link>
          }
          widthOfElement={columnHeaderWidth["asn no"] || 100}
        />
      ),
      "SHIPMENT NO": (
        <TextOnHoverWhenAdjust
          text={
            order?.consignment?.id ? (
              <Link
                to={`/shipment/po-to/${order.consignment.id}`}
                style={{ color: "#1249bf", fontWeight: 500 }}
              >
                {order.consignment.name}
              </Link>
            ) : (
              order?.consignment?.name || ""
            )
          }
          widthOfElement={columnHeaderWidth["shipment no"] || 120}
        />
      ),
      SUPPLIER: (
        <TextOnHoverWhenAdjust
          key="supplier"
          text={suppliers || notApplicable}
          widthOfElement={columnHeaderWidth["supplier"] || 150}
        />
      ),
      "PURCHASE ORDERS": (
        <td key="purchase_orders">
          <PrepaymentPurchaseOrdersCell
            order={order}
            width={columnHeaderWidth["purchase orders"] || 150}
          />
        </td>
      ),
      "TRANSFER ORDERS": (
        <td key="transfer_orders">
          <ShipmentTransferOrdersCell
            order={order}
            width={columnHeaderWidth["transfer orders"] || 150}
          />
        </td>
      ),
      "FROM FACILITY": (
        <TextOnHoverWhenAdjust
          key="from_facility"
          text={fromWarehouses || notApplicable}
          widthOfElement={columnHeaderWidth["from facility"] || 150}
        />
      ),
      "TO FACILITY": (
        <TextOnHoverWhenAdjust
          key="to_facility"
          text={toWarehouse || notApplicable}
          widthOfElement={columnHeaderWidth["to facility"] || 120}
        />
      ),
      "ORDER DATE": (
        <td key="date_of_creation" className="centerAlign" title={creationDateWithTime}>
          {creationDate || ""}
        </td>
      ),
      ETA: (
        <td key="eta" className="centerAlign" title={etaDateWithTime}>
          {etaDate || ""}
        </td>
      ),
      "CREATED BY": (
        <TextOnHoverWhenAdjust
          key="created_by"
          text={order?.created_by?.full_name || ""}
          widthOfElement={columnHeaderWidth["created by"] || 120}
        />
      ),
      STATUS: (
        <td key="status" className="centerAlign">
          <StatusTag className={statusName}>{statusName}</StatusTag>
        </td>
      ),
    };
  },
};

export const tableDataElements = ({
  order,
  orderType,
  tableHeaderList,
  allocation,
  columnHeaderWidth,
  formattedAmount,
  onTrackingUpdated,
  onOpenIncomingDrawer,
  recordActionHandlers,
}) => {
  const allElements = listDataElements[orderType]({
    order,
    allocationFor: allocation,
    columnHeaderWidth,
    formattedAmount,
    onTrackingUpdated,
    onOpenIncomingDrawer,
    recordActionHandlers,
  });

  return tableHeaderList.map(header => allElements[header.toUpperCase()]);
};

const selectNameForAllocation = {
  purchase: "purchase_order",
  transfer: "transfer_order",
  salesOrder: "sale_order",
  manufacturingOrder: "manufacturing_order",
};

export const listOfClasses = {
  "ORD NO": "leftAlign",
  "ORDER NAME": "leftAlign",
  PRODUCT: "leftAlign",
  "FROM FACILITY": "leftAlign",
  "TO FACILITY": "leftAlign",
  STATUS: "leftAlign",
  FACILITY: "leftAlign",
  ALLOCATED: "rightAlign allocatedTotal",
  "ALLOCATED QTY": "rightAlign allocatedTotal",
  ALLOCATABLE: "rightAlign",
  "ALLOCATABLE QTY": "rightAlign",
  DISPATCHED: "rightAlign",
  "ALCTD VALUE": "rightAlign",
  "ALCTD CBM": "rightAlign",
  REQUIRED: "rightAlign",
  QUANTITY: "rightAlign",
};

export const listOfClassesForHeader = {
  ACTIONS: "centerAlign",
  ALLOCATED: "rightAlign allocatedHeader",
  "ALLOCATED QTY": "rightAlign allocatedHeader",
};

export const qtyItemsForAllocation = [
  "REQUIRED",
  "ALLOCATED",
  "ALLOCATED QTY",
  "ALLOCATABLE",
  "ALLOCATABLE QTY",
  "DISPATCHED",
  "ALCTD VALUE",
  "ALCTD CBM",
  "SELLABLE",
  "RESERVED",
  "ON HOLD",
  "REJECTED",
  "TOTAL",
  "INCOMING",
  "PO TOTAL",
  "PAYMENTS",
  "OUTSTANDING",
  "NSP",
  "AVG COST",
  "STOCK VALUE",
  "QUANTITY",
  "SUGGESTED QUANTITY",
  "CONFIRM QTY",
  "SOH",
  "UNIT COST",
  "UNIT PRICE",
  "OTHER COSTS",
  "TOTAL LANDED COSTS",
  "CBM",
  "WIDTH",
  "WEIGHT",
  "LENGTH",
  "HEIGHT",
  "QTY/PACK",
  "FROM QUANTITY",
  "TO QUANTITY",
  "PRICE",
  "COSTING DECIMAL PLACES",
  "MIN ORDER QTY",
  "ORDER MULTIPLE",
  "REORDER POINT QTY",
  "SAFETY STOCK QTY",
  "SUB TOTAL",
  "TAX",
  "TOTAL IN CURRENCY",
  "TAX EXCLUDED",
  "AMOUNT",
  "CREDIT LIMIT AMOUNT",
  "TOTAL AMOUNT",
  "CUSTOMS VALUE",
  "INSURANCE VALUE",
  "REPLENISHMENT QUANTITY",
  "TRIGGER QUANTITY",
  "MAX STOCK QUANTITY",
  "REORDER POINT DAYS",
  "SAFETY STOCK DAYS",
  "SAFETY STOCK QTY",
  "PRODUCTION LEAD TIME DAYS",
  "AGEING DAYS",
  "TRANSIT LEAD TIME DAYS",
  "TOTAL LEAD TIME DAYS",
  "PLAN HORIZON DAYS",
  "TOTAL FREIGHT COST",
  "OUTPUT QUANTITY",
  "COST P/UNIT",
  "TOTAL COST",
  "LABOUR COST",
  "WASTAGE %",
];

const centerAlignHeader = [
  "ACTIONS",
  "DATE OF TRANSACTION",
  "REQ DATE",
  "EXPIRY DATE",
  "PACK DATE",
  "DANGEROUS GOODS",
  "REQUESTED SHIP DATE",
  "ETA",
  "ETD",
  "ATA",
  "ATD",
  "ORDER DATE",
  "CREATE DATE",
  "STATUS",
  "CONSOLIDATION STATUS",
  "FROM",
  "TO",
  "BILL DATE",
  "DUE DATE",
  "DATE",
  "TIME",
  "CREDIT DATE",
  "VALIDITY DATE",
  "PAYMENT STATUS",
  "POSTING DATE",
  "START DATE",
  "END DATE",
  "RETURN DATE",
  "DATE PAID",
];

export const classNameForHeader = header => {
  let classOfHeader = "";
  if (qtyItemsForAllocation.includes(header)) classOfHeader = "rightAlign";
  else if (centerAlignHeader.includes(header)) classOfHeader = "centerAlign";
  return classOfHeader;
};

export const MILESTONE_ACTIONS = {
  shipment: handleFilterShipmentByMilestone,
  transfer: handleFilterTransferByMilestone,
  purchase: handleFilterPurchaseByMilestone,
};

export const orderTypeForUrl = {
  product: {
    module: "product",
  },
  productCategory: {
    module: "product_category",
  },
  productTag: {
    module: "product_tag",
  },
  productAlias: {
    module: "product_alias",
  },
  productPlan: {
    module: "product_plan",
  },
  warehouse: {
    module: "warehouse",
  },
  supplier: {
    module: "supplier",
  },
  customer: {
    module: "customer",
  },
  pack: {
    module: "pack",
  },
  picklist: {
    module: "picklist",
  },
  warehouseCostingRange: {
    module: "whs_cost_range",
  },
  forecast: {
    module: "forecast",
  },
  stockLocation: {
    module: "stock_location",
  },
  warehouseCosting: {
    module: "whs_cost",
  },
  stockZone: {
    module: "stock_zone",
  },
  transactionReason: {
    module: "transaction_reason",
  },
  paymentTerm: {
    module: "payment_term",
  },
  billChargeType: {
    module: "bill_charge_type",
  },
  journalLine: {
    module: "journal_line",
  },
  standardCost: {
    module: "standard_costing",
  },
  taxRate: {
    module: "tax_rate",
  },
  site: {
    module: "site",
  },
  workCenter: {
    module: "work_center",
  },
  people: {
    module: "people",
  },
  users: {
    module: "users",
  },
  boms: {
    module: "bom",
  },
  manufacturingOrder: {
    module: "manufacturing_order",
  },
  coManufacturingOrder: {
    module: "co_manufacturing_order",
  },
  taxCategory: {
    module: "tax_category",
  },
  customerTag: {
    module: "customer_tag",
  },
  productTaxClassification: {
    module: "product_tax_classification",
  },
  customerSegment: {
    module: "customer_segment",
  },
  promo: {
    module: "promo",
  },
  unitOfMeasure: {
    module: "unit_of_measure",
  },
  equipmentType: {
    module: "equipment_type",
  },
  equipment: {
    module: "equipment",
  },
  salesRep: {
    module: "sales_rep",
  },
  chartsOfAccount: {
    module: "charts_account",
  },
  purchase: {
    module: "purchase_order",
  },
  receipt: {
    module: "receipt",
  },
  receiptAdjustment: {
    module: "receipt_adjustment",
  },
  bill: {
    module: "bill",
  },
  saleInvoice: {
    module: "sale_invoice",
  },
  syncHistory: {
    module: "integ_audit_log",
  },
  shopifyStores: {
    module: "shopify_store",
  },
  shopifyCatalogue: {
    module: "shopify_catalogue",
  },
  shopifyLinkedProducts: {
    module: "shopify_linked_product",
  },
  supplierCreditNote: {
    module: "supplier_credit_note",
  },
  customerCreditNote: {
    module: "customer_credit_note",
  },
  prePayment: {
    module: "prepayment",
  },
  stockReplenishment: {
    module: "stock_replenishment",
  },
  plannedOrder: {
    module: "planned_order",
  },
  shipment: {
    module: "consignment",
  },
  shipmentV1: {
    module: "shipment",
  },
  asn: {
    module: "asn",
  },
  transfer: {
    module: "transfer_order",
  },
  inventory: {
    module: "stock_summary",
  },
  inventoryDetail: {
    module: "stock_line",
  },
  inventoryTransactions: {
    module: "stock_transaction",
  },
  allocation: {
    purchase: { module: "po_allocation" },
    transfer: { module: "tf_ord_allocation" },
    salesOrder: { module: "so_allocation" },
    manufacturingOrder: { module: "mo_allocation" },
  },
  pricelist: {
    module: "pricelist",
  },
  quote: {
    module: "sale_quot",
  },
  salesOrder: {
    module: "sale_order",
  },
  customerReturn: {
    module: "return",
  },
  stockPack: {
    module: "stock_pack",
  },
  delivery: {
    module: "delivery",
  },
  shipmentLeg: {
    module: "shipment_leg",
  },
};

export const selectState = {
  purchase: "newPurchaseOrder",
  receipt: "receipt",
  shipment: "newInterface",
  transfer: "newTransferOrder",
  allocation: "newTransferOrder",
  inventory: "newWarehouse",
  inventoryDetail: "newWarehouse",
  inventoryTransactions: "newWarehouse",
  product: "product",
  stockReplenishment: "newWarehouse",
};

export const dataForSelectedState = {
  purchase: "purchaseOrders",
  shipment: "consignments",
  transfer: "transferOrders",
  inventory: "stockOnHand",
  inventoryDetail: "stockLines",
  inventoryTransactions: "transactions",
  shipment_po: "purchaseAllocations",
  shipment_to: "transferAllocations",
};

export const selectStateForOrder = {
  purchase: "purchaseOrder",
  shipment: "shipment",
  transfer: "order",
  inventory: "warehouse",
  inventoryDetail: "warehouse",
  inventoryTransactions: "report",
  allocation: "order",
  delivery: "shipment",
};
export const selectStateForNotes = {
  purchase: "purchaseOrder",
  shipment: "consignment",
  transfer: "transferOrder",
  delivery: "consignment",
};
export const headerForFilter = {
  purchase: "Purchase",
  shipment: "Shipment",
  transfer: "Transfer",
  delivery: "Delivery",
};

export const selectFilterFieldForHeader = {
  "order name": "name",
  "shipment no": "name",
  supplier: "name",
  "to whs": "name",
  "to facility": "name",
  "from whs": "name",
  status: "name",
  "created by": "first_name",
  product: "number",
  "product name": "name",
  description: "name",
  "ord no": "name",
  warehouse: "name",
  category: "name",
  reason: "reason",
  shipment: "reference",
  "shipment number": "shipment_number",
  "document number": "document_number",
  "receipt no": "name",
  number: "number",
  "purchase no": "name",
  type: "name",
  code: "code",
  rate: "rate",
  "product tax classification name": "name",
  phone: "phone_number",
  "sales rep": "full_name",
  "full name": "full_name",
  "sub type": "sub_type",
  tags: "tags",
  customer: "display_name",
};
export const moduleForView = {
  purchase: "purchase",
  shipment: "consignment",
  transfer: "transfer",
  inventory: "inventory",
  inventoryDetail: "inventoryDetail",
  inventoryTransactions: "inventoryTransactions",
  allocation: "allocation",
  consignment_file: "consignment_file",
  receipt: "receipt",
  receiptDetail: "receiptDetail",
  product: "product",
  productCategory: "productCategory",
  productPlan: "productPlan",
  productTag: "productTag",
  productAlias: "productAlias",
  warehouse: "warehouse",
  supplier: "supplier",
  pack: "pack",
  warehouseCostingRange: "warehouseCostingRange",
  forecast: "forecast",
  stockLocation: "stockLocation",
  warehouseCosting: "warehouseCosting",
  stockZone: "stockZone",
  picklist: "picklist",
  transactionReason: "transactionReason",
  paymentTerm: "paymentTerm",
  billChargeType: "billChargeType",
  journalLine: "journalLine",
  standardCost: "standardCost",
  taxRate: "taxRate",
  site: "site",
  workCenter: "workCenter",
  people: "people",
  users: "users",
  boms: "boms",
  manufacturingOrder: "manufacturingOrder",
  coManufacturingOrder: "coManufacturingOrder",
  taxCategory: "taxCategory",
  chartsOfAccount: "chartsOfAccount",
  syncHistory: "syncHistory",
  shopifyStores: "shopifyStores",
  shopifyCatalogue: "shopifyCatalogue",
  shopifyLinkedProducts: "shopifyLinkedProducts",
  bill: "bill",
  saleInvoice: "saleInvoice",
  supplierCreditNote: "supplierCreditNote",
  customerCreditNote: "customerCreditNote",
  receiptAdjustment: "receiptAdjustment",
  prePayment: "prePayment",
  stockReplenishment: "stockReplenishment",
  plannedOrder: "plannedOrder",
  customer: "customer",
  customerTag: "customerTag",
  customerSegment: "customerSegment",
  productTaxClassification: "productTaxClassification",
  unitOfMeasure: "unitOfMeasure",
  salesRep: "salesRep",
  pricelist: "pricelist",
  promo: "promo",
  quote: "quote",
  salesOrder: "salesOrder",
  stockPack: "stockPack",
  delivery: "delivery",
  shipmentV1: "shipmentV1",
  asn: "asn",
  equipmentType: "equipmentType",
  equipment: "equipment",
  shipmentLeg: "shipmentLeg",
  customerReturn: "customerReturn",
};

export const colorByStatus = {
  Created: "green",
  Confirmed: "blue",
  "Fully Received": "green",
  Received: "green",
  "Seller Location": "sky",
  "Partially Received": "orange",
  Cancelled: "red",
};

export const countMapWithHeader = {
  REQUIRED: "ordered_qty",
  ALLOCATED: "allocated_qty",
  "ALLOCATED QTY": "allocated_qty",
  ALLOCATABLE: "allocatable",
  "ALLOCATABLE QTY": "allocatable",
  DISPATCHED: "dispatched_qty",
  "ALCTD VALUE": "allocated_value",
  "ALCTD CBM": "allocated_cbm",
  QUANTITY: "quantity",
};

export const totalCountForData = {
  allocation: po_line => {
    let totalCount = {
      ordered_qty: 0,
      allocated_qty: 0,
      dispatched_qty: 0,
      allocated_value: 0,
      allocated_cbm: 0,
      allocatable: 0,
      req_quantity: 0,
      quantity: 0,
    };
    po_line.forEach(item => {
      totalCount = {
        quantity: totalCount.quantity + Number(item.qty || 0),
        ordered_qty: totalCount.ordered_qty + Number(item.ordered_qty),
        req_quantity: totalCount.req_quantity + Number(item?.required_qty || item.quantity),
        dispatched_qty: totalCount.dispatched_qty + Number(item.dispatched_qty),
        allocated_qty: totalCount.allocated_qty + Number(item.allocated_qty),
        allocatable: totalCount.allocatable + Number(item.allocatable ?? 0),
        allocated_value: totalCount.allocated_value + Number(item.allocated_value),
        allocated_cbm: totalCount.allocated_cbm + Number(item.allocated_cbm),
      };
    });
    return totalCount;
  },
};

export const qtyHeaders = [
  "required",
  "allocatable",
  "allocated",
  "dispatched",
  "alctd value",
  "alctd cbm",
  "from quantity",
  "to quantity",
  "price",
];

export const serverFieldForQty = {
  "required qty": "is_ordered_qty_zero",
  "allocatable qty": "is_allocatable_zero",
  "allocated qty": "is_allocated_qty_zero",
  "dispatched qty": "is_dispatched_qty_zero",
  "allocated value": "is_allocated_value_zero",
  "allocated cbm": "is_allocated_cbm_zero",
  "from quantity": "is_from_qty_zero",
  "to quantity": "is_to_qty_zero",
  price: "is_price_zero",
};

/** Zero / non-zero list filters keyed by orderType and lowercased column header. */
export const zeroFilterFieldsByModule = {
  inventory: {
    sellable: "is_sellable_zero",
    allocated: "is_allocated_zero",
    reserved: "is_reserved_zero",
    "on hold": "is_on_hold_zero",
    rejected: "is_rejected_zero",
    total: "is_total_zero",
    nsp: "is_nsp_zero",
    "avg cost": "is_average_cost_zero",
    "stock value": "is_stock_value_zero",
  },
  inventoryDetail: {
    quantity: "is_qty_zero",
    allocated: "is_allocated_qty_zero",
    allocatable: "is_allocatable_qty_zero",
    "unit cost": "is_unit_price_zero",
    "other costs": "is_landed_cost_zero",
    "total landed costs": "is_total_unit_price_zero",
  },
  inventoryTransactions: {
    quantity: "is_qty_zero",
    soh: "is_stock_qty_zero",
  },
};

export const getZeroFilterParam = (orderType, headerName) =>
  zeroFilterFieldsByModule[orderType]?.[headerName];

export const headersNameForFilter = [
  "SHIPMENT NAME",
  "SHIPMENT NO",
  "SUPPLIER",
  "FROM FACILITY",
  "TO FACILITY",
  "ORDER DATE",
  "CREATE DATE",
  "ETA",
  "ETD",
  "ATA",
  "ATD",
  "CREATED BY",
  "STATUS",
  "REP STATUS",
  "PRODUCT",
  "FACILITY",
  "REASON",
  "DATE OF TRANSACTION",
  "DESCRIPTION",
  "ORD NO",
  "ORDER NAME",
  "REQUIRED DATE",
  "NAME",
  "REQ DATE",
  "CATEGORY",
  "PRODUCT NAME",
  "RECEIPT NO",
  "POSTING DATE",
  "SHIPMENT",
  "DOCUMENT NUMBER",
  "SHIPMENT NUMBER",
  "ERP SUPPLIER",
  "CONSOLIDATION STATUS",
  "PURCHASE NO",
  "TRANSFER NO",
  "PARENT CATEGORY",
  "CODE",
  "NUMBER",
  "FROM",
  "TO",
  "FROM QUANTITY",
  "TO QUANTITY",
  "PRICE",
  "BUYING CURRENCY",
  "SELLING CURRENCY",
  "TYPE",
  "TYPES",
  "SITE",
  "STOCK ZONE",
  "PLANNING POLICY",
  "ACQUISITION TYPE",
  "IMPACT",
  "OTHER COSTS TYPE",
  "TERM TYPE",
  "JURISDICTION",
  "BILL NO",
  "BILL DATE",
  "DUE DATE",
  "SUPPLIER REFERENCE",
  "RECEIPT REFERENCE",
  "CURRENCY",
  "DATE",
  "APPLICATION",
  "CREDIT NO",
  "CREDIT DATE",
  "RECEIPT ADJUSTMENT NO",
  "PAYMENT NO",
  "PREPAYMENT NO",
  "REFERENCE",
  "PAYMENT TYPE",
  "DATE PAID",
  "BANK ACCOUNT",
  "ACCOUNT",
  "PURCHASE ORDERS",
  "RECEIPTS",
  "TRANSFER ORDERS",
  "SHIPMENTS",
  "REPLENISH NO",
  "DISPLAY NAME",
  "FULL NAME",
  "TAX CATEGORY NAME",
  "TAX CATEGORY",
  "PRODUCT TAX CLASSIFICATION NAME",
  "DEFAULT PAYMENT TERM",
  "DEFAULT SALE ACCOUNT",
  "SALES REP",
  "DEFAULT TAX RATE",
  "TAGS",
  "SUB TYPE",
  "SYSTEM DEFAULT",
  "SCOPE",
  "PRICELIST NAME",
  "COUNTRY",
  "SOURCE COUNTRY",
  "CHANNEL",
  "SUPPLYING FACILITY",
  "VALIDITY DATE",
  "DISCOUNT TYPE",
  "TITLE",
  "QUOTE NUMBER",
  "CUSTOMER",
  "CUSTOMERS",
  "EXPIRY DATE",
  "SALES NO",
  "SALES ORDERS",
  "REQUESTED SHIP DATE",
  "ORDER DATE",
  "PICKLIST NO",
  "PACK NO",
  "SUPPLY FACILITY",
  "DELIVERY NO",
  "ASN NO",
  "RETURN NO",
  "EQUIPMENT TYPE",
  "INVOICE NO",
  "FULFILLMENT",
  "CO MANUFACTURER",
  "LINKED TO XERO",
  "BOM NO",
  "MO NO",
  "PLANNED START",
  "REQUIRED BY",
  "EST COMPLETION",
  "MANF NO",
  "PO TOTAL",
  "PAYMENTS",
  "OUTSTANDING",
  "PAYMENT STATUS",
];

export const headersNameForFilterByModule = {
  asn: {
    filter: [
      "ASN NO",
      "SHIPMENT NO",
      "SUPPLIER",
      "FROM FACILITY",
      "TO FACILITY",
      "PURCHASE ORDERS",
      "TRANSFER ORDERS",
      "ORDER DATE",
      "ETA",
      "CREATED BY",
      "STATUS",
    ],
    sort: ["ASN NO", "SHIPMENT NO", "ORDER DATE", "CREATED BY", "STATUS"],
  },

  shopifyCatalogue: {
    filter: ["PRODUCT", "PRICELIST", "FACILITIES", "AVAILABLE STOCK", "ALLOW BACKORDER", "STATUS"],
    sort: ["PRODUCT", "PRICELIST", "FACILITIES", "AVAILABLE STOCK", "ALLOW BACKORDER", "STATUS"],
  },

  shopifyLinkedProducts: {
    filter: [],
    sort: [
      "PRODUCT",
      "SHOPIFY PRODUCT",
      "SHOPIFY AVAILABLE",
      "SHOPIFY ON HAND",
      "ON ORDER",
      "NEXT ETA",
    ],
  },

  workCenter: {
    filter: [
      "NAME",
      "CODE",
      "TYPE",
      "LOCATION",
      "FACILITY",
      "STOCK ZONE",
      "CREATED BY",
      "CREATE DATE",
    ],
    sort: [
      "NAME",
      "CODE",
      "TYPE",
      "LOCATION",
      "FACILITY",
      "STOCK ZONE",
      "CREATED BY",
      "CREATE DATE",
    ],
  },
  product: {
    filter: ["NUMBER", "NAME", "TYPE", "CATEGORIES", "TAGS", "CREATED BY", "CREATE DATE", "STATUS"],
    sort: ["NUMBER", "NAME", "TYPE", "CREATED BY", "CREATE DATE", "STATUS"],
  },
  supplier: {
    filter: ["NAME", "CODE", "LINKED TO XERO", "CO MANUFACTURER", "CURRENCY", "CREATED BY"],
    sort: [
      "NAME",
      "CODE",
      "LINKED TO XERO",
      "CO MANUFACTURER",
      "EMAIL",
      "CONTACT",
      "ADDRESS",
      "CURRENCY",
      "PAYMENT TERMS",
      "CREATED BY",
    ],
  },
  customer: {
    filter: [
      "CODE",
      "DISPLAY NAME",
      "LINKED TO XERO",
      "TYPE",
      "EMAIL",
      "CURRENCY",
      "DEFAULT PAYMENT TERM",
      "DEFAULT SALE ACCOUNT",
      "SUB TYPE",
      "TAGS",
      "SALES REP",
      "DEFAULT TAX RATE",
      "CREATED BY",
      "CREATE DATE",
      "STATUS",
    ],
    sort: [
      "CODE",
      "DISPLAY NAME",
      "LINKED TO XERO",
      "TYPE",
      "EMAIL",
      "CURRENCY",
      "DEFAULT PAYMENT TERM",
      "DEFAULT SALE ACCOUNT",
      "SUB TYPE",
      "TAGS",
      "SALES REP",
      "DEFAULT TAX RATE",
      "CREATED BY",
      "CREATE DATE",
      "STATUS",
    ],
  },
  chartsOfAccount: {
    filter: ["NAME", "CODE", "ACCOUNT TYPE", "CURRENCY", "CREATED BY"],
    sort: ["NAME", "CODE", "ACCOUNT TYPE", "CURRENCY", "CREATED BY"],
  },
  taxRate: {
    filter: ["NAME", "TAX CATEGORY", "JURISDICTION", "SYSTEM DEFAULT", "CREATED BY", "CREATE DATE"],
    sort: ["NAME", "JURISDICTION", "CREATED BY", "CREATE DATE"],
  },
  taxCategory: {
    filter: ["TAX CATEGORY NAME", "SYSTEM DEFAULT"],
    sort: ["TAX CATEGORY NAME", "SYSTEM DEFAULT"],
  },
  productTaxClassification: {
    filter: ["PRODUCT TAX CLASSIFICATION NAME", "SYSTEM DEFAULT"],
    sort: ["PRODUCT TAX CLASSIFICATION NAME", "SYSTEM DEFAULT"],
  },
  customerSegment: {
    filter: ["CUSTOMER SEGMENT NAME", "STATUS"],
    sort: ["CUSTOMER SEGMENT NAME", "STATUS"],
  },
  people: {
    filter: ["NAME", "ROLE", "CREATED BY", "CREATE DATE"],
    sort: ["NAME", "ROLE", "CREATED BY", "CREATE DATE"],
  },
  users: {
    filter: ["NAME", "EMAIL", "ROLE", "COMPANY", "PARTNER", "STATUS"],
    sort: ["NAME", "EMAIL", "ROLE", "COMPANY", "PARTNER", "STATUS", "JOINED", "LAST LOGIN"],
  },
  paymentTerm: {
    filter: ["NAME", "TERM TYPE", "DUE DAYS", "MONTH OFFSET", "ACTIVE", "DEFAULT", "CREATED BY"],
    sort: ["NAME", "TERM TYPE", "DUE DAYS", "MONTH OFFSET", "ACTIVE", "DEFAULT", "CREATED BY"],
  },
  billChargeType: {
    filter: [
      "NAME",
      "AMOUNT TYPE",
      "DISTRIBUTION TYPE",
      "IS LANDED COST",
      "CREATED BY",
      "DATE OF CREATION",
    ],
    sort: [
      "NAME",
      "AMOUNT TYPE",
      "DISTRIBUTION TYPE",
      "UNIT PRICE",
      "IS LANDED COST",
      "CREATED BY",
      "DATE OF CREATION",
    ],
  },
  journalLine: {
    filter: ["ACCOUNT CODE", "ACCOUNT NAME", "CREATE DATE"],
    sort: ["ACCOUNT CODE", "ACCOUNT NAME", "CREATE DATE", "AMOUNT"],
  },
  coManufacturingOrder: {
    filter: [
      "CO-MANF NO",
      "CO-MANF",
      "CO-MANF WHS",
      "ETA",
      "PRIORITY",
      "CURRENCY",
      "CREATED BY",
      "DESTINATION WHS",
      "STATUS",
    ],
    sort: [
      "CO-MANF NO",
      "CO-MANF",
      "CO-MANF WHS",
      "ETA",
      "PRIORITY",
      "CURRENCY",
      "CREATED BY",
      "DESTINATION WHS",
      "STATUS",
    ],
  },

  pricelist: {
    filter: [
      "PRICELIST NAME",
      "COUNTRY",
      "CHANNEL",
      "SUPPLYING FACILITY",
      "VALIDITY DATE",
      "STATUS",
      "CREATE DATE",
    ],
    sort: ["PRICELIST NAME", "VALIDITY DATE", "STATUS", "CREATE DATE"],
  },
  shipmentLeg: {
    filter: [
      "SHIPMENT NAME",
      "SEQUENCE",
      "STATUS",
      "TYPE",
      "TRANSPORT MODE",
      "CARRIER",
      "VEHICLE / VESSEL",
      "ORIGIN",
      "DESTINATION",
      "DELIVER TO",
      "ETD",
      "ETA",
      "ATD",
      "ATA",
    ],
    sort: [
      "SHIPMENT NAME",
      "SEQUENCE",
      "STATUS",
      "TYPE",
      "TRANSPORT MODE",
      "CARRIER",
      "VEHICLE / VESSEL",
      "ORIGIN",
      "DESTINATION",
      "DELIVER TO",
      "ETD",
      "ETA",
      "ATD",
      "ATA",
    ],
  },
  inventory: {
    filter: [
      "CATEGORIES",
      "PRODUCT",
      "FACILITY",
      "SELLABLE",
      "ALLOCATED",
      "RESERVED",
      "ON HOLD",
      "REJECTED",
      "TOTAL",
      "NSP",
      "AVG COST",
      "STOCK VALUE",
    ],
    sort: [
      "PRODUCT",
      "FACILITY",
      "SELLABLE",
      "ALLOCATED",
      "RESERVED",
      "ON HOLD",
      "REJECTED",
      "TOTAL",
      "NSP",
      "AVG COST",
      "STOCK VALUE",
    ],
  },
  inventoryDetail: {
    filter: [
      "PRODUCT",
      "FACILITY",
      "STATUS",
      "CREATE DATE",
      "CREATED BY",
      "QUANTITY",
      "ALLOCATED",
      "ALLOCATABLE",
      "UNIT COST",
      "OTHER COSTS",
      "TOTAL LANDED COSTS",
    ],
    sort: [
      "PRODUCT",
      "FACILITY",
      "QUANTITY",
      "ALLOCATED",
      "ALLOCATABLE",
      "UNIT COST",
      "OTHER COSTS",
      "TOTAL LANDED COSTS",
      "BATCH NO",
      "SERIAL NO",
      "LOCATION",
      "ZONE",
      "STATUS",
      "CREATE DATE",
    ],
  },
  inventoryTransactions: {
    filter: ["PRODUCT", "FACILITY", "QUANTITY", "SOH", "REASON", "DATE OF TRANSACTION"],
    sort: ["PRODUCT", "FACILITY", "QUANTITY", "SOH", "REASON", "REFERENCE", "DATE OF TRANSACTION"],
  },
  warehouse: {
    filter: [
      "NAME",
      "CODE",
      "SITE",
      "TYPES",
      "COUNTRY",
      "SHORT DESCRIPTION",
      "LONG DESCRIPTION",
      "CREATED BY",
      "STATE",
      "ADDRESS",
    ],
    sort: [
      "NAME",
      "CODE",
      "SITE",
      "COUNTRY",
      "SHORT DESCRIPTION",
      "LONG DESCRIPTION",
      "CREATED BY",
      "STATE",
      "ADDRESS",
      "TRANSIT AIR",
      "TRANSIT OCEAN",
    ],
  },
  warehouseCosting: {
    filter: ["PRODUCT", "SUPPLIER", "FROM FACILITY", "TO FACILITY", "CREATED BY", "CREATE DATE"],
    sort: [
      "PRODUCT",
      "SUPPLIER",
      "FROM FACILITY",
      "TO FACILITY",
      "FROM QUANTITY",
      "TO QUANTITY",
      "PRICE",
      "CREATED BY",
      "CREATE DATE",
    ],
  },
};

export function debounce(func, delay) {
  let timeoutId;

  return function (...args) {
    const context = this;
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
}

export const COLUMN_CONSTRAINTS = {
  MIN_WIDTH: 60,
  MAX_WIDTH: 600,
  DEFAULT_WIDTH: 150,
};

export const mapItemPerPageWithModule = {
  shipment: "international_shipments_items_per_page",
  inventory: "inventory_stock_summary_items_per_page",
  inventoryDetail: "inventory_stock_line_items_per_page",
  inventoryTransactions: "inventory_transactions_items_per_page",
  allocation: "purchase_allocations_items_per_page",
  purchase: "po_items_per_page",
  transfer: "transfer_orders_items_per_page",
  receipt: "receipts_items_per_page",
  receiptAdjustment: "receipt_adjustment_items_per_page",
  product: "products_items_per_page",
  productCategory: "product_category_items_per_page",
  productTag: "product_tag_items_per_page",
  productAlias: "product_alias_items_per_page",
  productPlan: "product_plan_items_per_page",
  warehouse: "warehouse_items_per_page",
  supplier: "supplier_items_per_page",
  pack: "pack_items_per_page",
  warehouseCostingRange: "warehouse_costing_range_items_per_page",
  forecast: "forecast_items_per_page",
  stockLocation: "stock_location_items_per_page",
  warehouseCosting: "warehouse_costing_items_per_page",
  stockZone: "stock_zone_items_per_page",
  transactionReason: "transaction_reason_items_per_page",
  standardCost: "standard_cost_items_per_page",
  taxRate: "tax_rate_items_per_page",
  site: "site_items_per_page",
  workCenter: "work_center_items_per_page",
  people: "people_items_per_page",
  users: "users_items_per_page",
  boms: "boms_items_per_page",
  manufacturingOrder: "manufacturing_order_items_per_page",
  coManufacturingOrder: "co_manufacturing_order_items_per_page",
  taxCategory: "tax_category_items_per_page",
  chartsOfAccount: "charts_account_items_per_page",
  paymentTerm: "payment_term_items_per_page",
  billChargeType: "bill_charge_type_items_per_page",
  journalLine: "journal_line_items_per_page",
  bill: "bill_items_per_page",
  saleInvoice: "sale_invoice_items_per_page",
  syncHistory: "sync_history_items_per_page",
  shopifyStores: "shopify_store_items_per_page",
  shopifyCatalogue: "shopify_catalogue_items_per_page",
  shopifyLinkedProducts: "shopify_linked_product_items_per_page",
  supplierCreditNote: "supplier_credit_note_items_per_page",
  customerCreditNote: "customer_credit_note_items_per_page",
  prePayment: "pre_payment_items_per_page",
  stockReplenishment: "stock_replenishment_items_per_page",
  plannedOrder: "planned_orders_items_per_page",
  customer: "customer_items_per_page",
  picklist: "picklist_items_per_page",
  customerTag: "customer_tag_items_per_page",
  customerSegment: "customer_segment_items_per_page",
  productTaxClassification: "product_tax_classification_items_per_page",
  unitOfMeasure: "unit_of_measure_items_per_page",
  salesRep: "sales_rep_items_per_page",
  pricelist: "pricelist_items_per_page",
  promo: "promo_items_per_page",
  quote: "quote_items_per_page",
  salesOrder: "order_items_per_page",
  stockPack: "stock_pack_items_per_page",
  delivery: "delivery_items_per_page",
  shipmentV1: "shipmentV1_items_per_page",
  asn: "asn_items_per_page",
  shipmentLeg: "shipment_leg_items_per_page",
  equipmentType: "equipment_type_items_per_page",
  equipment: "equipment_items_per_page",
  customerReturn: "customer_return_items_per_page",
};

export const mapDateFilterId = {
  "date of creation": "date_of_creation",
  "date of transaction": "date_of_creation",
  "create date": "create_date",
  "posting date": "posting_date",
  from: "from_date",
  to: "to_date",
  "bill date": "create_date",
  "due date": "due_date",
  "required date": "required_date",
  "credit date": "credit_date",
  "date paid": "date_paid",
  "order date": "order_date",
  "requested ship date": "requested_ship_date",
  "invoice date": "invoice_date",
  "planned start": "planned_start",
  "required by": "required_by",
  "est completion": "estimated_completion",
};

export const detailPageUrl = {
  shipment: "shipment/po-to",
  purchase: "orders/purchase",
  transfer: "transfer_order",
  quote: "sales/quotation",
  salesOrder: "sales/order",
  picklist: "picklist",
  stockPack: "stock_packs",
  delivery: "delivery",
  asn: "shipment/asn",
};
